'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type SiteLang = 'fr' | 'en' | 'ar' | 'es';

export const SITE_LANGS: { code: SiteLang; label: string; short: string }[] = [
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'AR' },
];

const FRENCH_WORDS = new Set(['salut', 'bonjour', 'cherche', 'voiture', 'voitures', 'places', 'devis', 'semaine', 'besoin', 'merci', 'faut']);
const ENGLISH_WORDS = new Set(['hello', 'hi', 'hey', 'need', 'cheap', 'car', 'cars', 'seats', 'quote', 'week', 'looking', 'please', 'thanks', 'budget', 'desert']);
const SPANISH_WORDS = new Set(['hola', 'busco', 'coche', 'coches', 'necesito', 'necesitamos', 'presupuesto', 'plazas', 'barato', 'quiero', 'gracias', 'semana', 'desierto']);

/** The site language a client message is written in, when one language is clearly ahead. */
export function detectSiteLang(message: string): SiteLang | null {
  const text = message.trim();
  if (text.length < 2) return null;
  if (/[\u0600-\u06FF]/.test(text)) return 'ar';

  const scores = { fr: 0, en: 0, es: 0 };
  if (/[ñ¿¡]/i.test(text)) scores.es += 2;
  if (/[éèêëàâçôùûïî]/i.test(text)) scores.fr += 2;

  const tokens = text.toLowerCase().split(/[^\p{L}]+/u).filter((token) => token.length > 1);
  const lists = { fr: FRENCH_WORDS, en: ENGLISH_WORDS, es: SPANISH_WORDS };
  for (const token of tokens) {
    for (const code of ['fr', 'en', 'es'] as const) {
      if (lists[code].has(token)) scores[code] += 1;
    }
  }

  const ranked: Array<'fr' | 'en' | 'es'> = ['fr', 'en', 'es'];
  ranked.sort((left, right) => scores[right] - scores[left]);
  const best = ranked[0];
  const runnerUp = ranked[1];
  if (scores[best] === 0 || scores[best] === scores[runnerUp]) return null;
  return best;
}

// Flat key → translated string. Keys are namespaced per component
// (nav.*, hero.*, search.*, ...). English is the fallback.
const DICTS: Record<SiteLang, Record<string, string>> = {
  en: {},
  fr: {},
  ar: {},
  es: {},
};

type Dict = Record<string, string>;
function add(lang: SiteLang, entries: Dict): void {
  Object.assign(DICTS[lang], entries);
}

// ---------------- nav ----------------
add('en', {
  'nav.home': 'HOME', 'nav.vehicles': 'VEHICLES', 'nav.locations': 'LOCATIONS',
  'nav.deals': 'DEALS', 'nav.services': 'SERVICES', 'nav.about': 'ABOUT US',
  'nav.contact': 'CONTACT US', 'nav.bookNow': 'BOOK NOW', 'nav.bookNowTitle': 'Book Now',
  'nav.profile': 'Profile', 'nav.myProfile': 'My Profile',
  'nav.drawerProfile': 'Profile dashboard', 'nav.drawerAdmin': 'Fleet management',
  'nav.support': '24/7 Customer Support', 'nav.freeCancel': 'Free Cancellation',
  'nav.bestPrice': 'Best Price Guarantee',
});
add('fr', {
  'nav.home': 'ACCUEIL', 'nav.vehicles': 'VÉHICULES', 'nav.locations': 'AGENCES',
  'nav.deals': 'OFFRES', 'nav.services': 'SERVICES', 'nav.about': 'À PROPOS',
  'nav.contact': 'CONTACT', 'nav.bookNow': 'RÉSERVER', 'nav.bookNowTitle': 'Réserver',
  'nav.profile': 'Profil', 'nav.myProfile': 'Mon profil',
  'nav.drawerProfile': 'Tableau de bord', 'nav.drawerAdmin': 'Gestion de la flotte',
  'nav.support': 'Assistance 24/7', 'nav.freeCancel': 'Annulation gratuite',
  'nav.bestPrice': 'Meilleur prix garanti',
});
add('ar', {
  'nav.home': 'الرئيسية', 'nav.vehicles': 'السيارات', 'nav.locations': 'الوكالات',
  'nav.deals': 'العروض', 'nav.services': 'الخدمات', 'nav.about': 'من نحن',
  'nav.contact': 'اتصل بنا', 'nav.bookNow': 'احجز الآن', 'nav.bookNowTitle': 'احجز الآن',
  'nav.profile': 'الحساب', 'nav.myProfile': 'حسابي',
  'nav.drawerProfile': 'لوحة التحكم', 'nav.drawerAdmin': 'إدارة الأسطول',
  'nav.support': 'دعم على مدار الساعة', 'nav.freeCancel': 'إلغاء مجاني',
  'nav.bestPrice': 'ضمان أفضل سعر',
});
add('es', {
  'nav.home': 'INICIO', 'nav.vehicles': 'VEHÍCULOS', 'nav.locations': 'AGENCIAS',
  'nav.deals': 'OFERTAS', 'nav.services': 'SERVICIOS', 'nav.about': 'NOSOTROS',
  'nav.contact': 'CONTACTO', 'nav.bookNow': 'RESERVAR', 'nav.bookNowTitle': 'Reservar',
  'nav.profile': 'Perfil', 'nav.myProfile': 'Mi perfil',
  'nav.drawerProfile': 'Panel de usuario', 'nav.drawerAdmin': 'Gestión de flota',
  'nav.support': 'Soporte 24/7', 'nav.freeCancel': 'Cancelación gratuita',
  'nav.bestPrice': 'Mejor precio garantizado',
});

interface LocaleCtx {
  lang: SiteLang;
  setLang: (l: SiteLang) => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
}

const LocaleContext = createContext<LocaleCtx>({
  lang: 'fr',
  setLang: () => {},
  t: (k) => DICTS.en[k] ?? k,
  dir: 'ltr',
});

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<SiteLang>('fr');

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const saved = localStorage.getItem('fleetava_locale') as SiteLang | null;
        if (saved === 'fr' || saved === 'en' || saved === 'ar') setLangState(saved);
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    } catch {}
  }, [lang]);

  const setLang = (l: SiteLang) => {
    setLangState(l);
    try {
      localStorage.setItem('fleetava_locale', l);
    } catch {}
  };

  const value = useMemo<LocaleCtx>(
    () => ({
      lang,
      setLang,
      t: (key: string) => DICTS[lang][key] ?? DICTS.en[key] ?? key,
      dir: lang === 'ar' ? 'rtl' : 'ltr',
    }),
    [lang],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}


// ---------------- hero ----------------
add('en', {
  'hero.titleA': 'Find The Perfect Car', 'hero.titleB': 'For Every', 'hero.titleC': 'Journey',
  'hero.sub': 'Explore our wide range of vehicles and enjoy the freedom to travel your way.',
  'hero.cta': 'BOOK YOUR RIDE',
  'hero.badge': 'Dacia Logan · Economic & Reliable',
});
add('fr', {
  'hero.titleA': 'Trouvez La Voiture Idéale', 'hero.titleB': 'Pour Chaque', 'hero.titleC': 'Voyage',
  'hero.sub': 'Explorez notre large gamme de véhicules et voyagez en toute liberté.',
  'hero.cta': 'RÉSERVER MON TRAJET',
  'hero.badge': 'Dacia Logan · Économique & Fiable',
});
add('ar', {
  'hero.titleA': 'اعثر على السيارة المثالية', 'hero.titleB': 'لكل', 'hero.titleC': 'رحلة',
  'hero.sub': 'اكتشف مجموعتنا الواسعة من السيارات واستمتع بحرية السفر على طريقتك.',
  'hero.cta': 'احجز رحلتك',
  'hero.badge': 'داسيا لوغان · اقتصادية وموثوقة',
});
add('es', {
  'hero.titleA': 'Encuentra El Coche Perfecto', 'hero.titleB': 'Para Cada', 'hero.titleC': 'Viaje',
  'hero.sub': 'Explora nuestra amplia gama de vehículos y disfruta la libertad de viajar a tu manera.',
  'hero.cta': 'RESERVA TU VIAJE',
  'hero.badge': 'Dacia Logan · Económico y fiable',
});

// ---------------- value props ----------------
add('en', {
  'vp.1t': 'Best Price Guarantee', 'vp.1d': 'We offer the best prices on the market.',
  'vp.2t': 'Free Cancellation', 'vp.2d': 'Cancel or modify your booking for free anytime.',
  'vp.3t': 'Wide Selection', 'vp.3d': 'Choose from a wide range of vehicles.',
  'vp.4t': '24/7 Support', 'vp.4d': "We're here to help you anytime, anywhere.",
});
add('fr', {
  'vp.1t': 'Meilleur prix garanti', 'vp.1d': 'Nous offrons les meilleurs prix du marché.',
  'vp.2t': 'Annulation gratuite', 'vp.2d': 'Annulez ou modifiez sans frais à tout moment.',
  'vp.3t': 'Large choix', 'vp.3d': 'Choisissez parmi un large éventail de véhicules.',
  'vp.4t': 'Assistance 24/7', 'vp.4d': 'Nous sommes là pour vous aider à tout moment.',
});
add('ar', {
  'vp.1t': 'ضمان أفضل سعر', 'vp.1d': 'نقدم أفضل الأسعار في السوق.',
  'vp.2t': 'إلغاء مجاني', 'vp.2d': 'ألغِ أو عدّل حجزك مجاناً في أي وقت.',
  'vp.3t': 'تشكيلة واسعة', 'vp.3d': 'اختر من بين مجموعة واسعة من السيارات.',
  'vp.4t': 'دعم 24/7', 'vp.4d': 'نحن هنا لمساعدتك في أي وقت ومكان.',
});
add('es', {
  'vp.1t': 'Mejor precio garantizado', 'vp.1d': 'Ofrecemos los mejores precios del mercado.',
  'vp.2t': 'Cancelación gratuita', 'vp.2d': 'Cancela o modifica tu reserva gratis cuando quieras.',
  'vp.3t': 'Amplia selección', 'vp.3d': 'Elige entre una amplia gama de vehículos.',
  'vp.4t': 'Soporte 24/7', 'vp.4d': 'Estamos aquí para ayudarte en todo momento.',
});

// ---------------- testimonials ----------------
add('en', {
  'testi.kicker': 'TESTIMONIALS', 'testi.title': 'What Our Customers Say',
});
add('fr', {
  'testi.kicker': 'TÉMOIGNAGES', 'testi.title': 'Ce Que Disent Nos Clients',
});
add('ar', {
  'testi.kicker': 'شهادات العملاء', 'testi.title': 'ماذا يقول عملاؤنا',
});
add('es', {
  'testi.kicker': 'TESTIMONIOS', 'testi.title': 'Lo Que Dicen Nuestros Clientes',
});


// ---------------- design: editorial + road trip ----------------
add('en', {
  'ed.kicker': 'RENT A CAR',
  'ed.titleA': 'Easy and fast way to', 'ed.titleB': 'rent your car',
  'ed.sub': 'Choose from a modern fleet, book in a couple of minutes and pick the car up wherever you land.',
  'ed.cta': 'BROWSE THE FLEET',
  'ed.inventory.kicker': 'LATEST INVENTORY', 'ed.inventory.title': 'Cars ready to drive',
  'ed.inventory.viewAll': 'VIEW ALL CARS',
  'ed.why.kicker': 'WHY CHOOSE US', 'ed.why.title': 'Everything you need for an easy rental',
  'ed.why.1t': 'Transparent pricing', 'ed.why.1d': 'The price you see includes insurance and assistance.',
  'ed.why.2t': 'Well maintained cars', 'ed.why.2d': 'Every vehicle is serviced and cleaned before pickup.',
  'ed.why.3t': 'Fast pickup', 'ed.why.3d': 'Your contract is ready when you arrive at the desk.',
  'ed.why.4t': 'Flexible cancellation', 'ed.why.4d': 'Plans change — cancel free of charge up to 24h before.',
  'ed.num.kicker': 'OUR NUMBERS',
  'ed.num.vehicles': 'Cars in the fleet', 'ed.num.categories': 'Vehicle categories', 'ed.num.locations': 'Pick-up points',

  'rt.tab1': 'Pick-up & return', 'rt.tab2': 'One way', 'rt.tab3': 'Airport transfer',
  'rt.pickupLoc': 'Pick-up location', 'rt.returnLoc': 'Return location',
  'rt.driverAge': 'Driver age', 'rt.passengers': 'Passengers',
  'rt.search': 'SEARCH CARS',
  'rt.trust1': 'No hidden fees', 'rt.trust2': 'Free cancellation', 'rt.trust3': '24/7 support',
  'rt.heroTitleA': 'More space. More memories.', 'rt.heroTitleB': 'better together.',
  'rt.sub': 'SUVs, vans and 4x4s for families, friends and long road trips across the country.',
  'rt.fleet.kicker': 'OUR FLEET', 'rt.fleet.title': 'Our fleet categories', 'rt.fleet.sub': 'Pick the size that fits your trip.',
  'rt.fleet.from': 'from', 'rt.fleet.cars': 'cars', 'rt.fleet.view': 'VIEW CARS',
  'rt.safety.kicker': 'SAFETY & COMFORT', 'rt.safety.title': 'Safety and comfort, always our priority',
  'rt.safety.1t': 'Full insurance', 'rt.safety.1d': 'Comprehensive cover on every rental.',
  'rt.safety.2t': 'Roadside assistance', 'rt.safety.2d': 'Help anywhere in the country, day or night.',
  'rt.safety.3t': 'Child seats', 'rt.safety.3d': 'Baby and booster seats on request.',
  'rt.safety.4t': 'Unlimited mileage', 'rt.safety.4d': 'Drive as far as your trip takes you.',
  'rt.safety.5t': 'Sanitised interiors', 'rt.safety.5d': 'Deep cleaned between every rental.',
  'rt.pack.kicker': 'TRAVEL PACKAGES', 'rt.pack.title': 'Popular travel packages', 'rt.pack.sub': 'Ready-made offers for a weekend or a full tour.',
  'rt.pack.cta': 'VIEW PACKAGE',
  'rt.faq.kicker': 'FAQS', 'rt.faq.title': 'Frequently asked questions',
  'rt.app.kicker': 'YOUR JOURNEY, MADE EASY', 'rt.app.title': 'Book, extend or cancel from your phone',
  'rt.app.sub': 'Call the desk for anything else — we answer every day.',
  'rt.app.call': 'Call us', 'rt.app.whatsapp': 'WhatsApp',
});
add('fr', {
  'ed.kicker': 'LOCATION DE VOITURE',
  'ed.titleA': 'Simple et rapide pour', 'ed.titleB': 'louer votre voiture',
  'ed.sub': 'Choisissez dans une flotte récente, réservez en deux minutes et récupérez la voiture où vous arrivez.',
  'ed.cta': 'VOIR LA FLOTTE',
  'ed.inventory.kicker': 'DERNIERS ARRIVAGES', 'ed.inventory.title': 'Des voitures prêtes à partir',
  'ed.inventory.viewAll': 'VOIR TOUTES LES VOITURES',
  'ed.why.kicker': 'POURQUOI NOUS CHOISIR', 'ed.why.title': 'Tout pour une location simple',
  'ed.why.1t': 'Prix transparent', 'ed.why.1d': 'Le prix affiché inclut assurance et assistance.',
  'ed.why.2t': 'Voitures entretenues', 'ed.why.2d': 'Chaque véhicule est révisé et nettoyé avant le retrait.',
  'ed.why.3t': 'Retrait rapide', 'ed.why.3d': 'Votre contrat est prêt dès votre arrivée.',
  'ed.why.4t': 'Annulation flexible', 'ed.why.4d': 'Les plans changent — annulez sans frais jusqu’à 24h avant.',
  'ed.num.kicker': 'NOS CHIFFRES',
  'ed.num.vehicles': 'Voitures dans la flotte', 'ed.num.categories': 'Catégories de véhicules', 'ed.num.locations': 'Points de retrait',

  'rt.tab1': 'Retrait & retour', 'rt.tab2': 'Aller simple', 'rt.tab3': 'Navette aéroport',
  'rt.pickupLoc': 'Lieu de retrait', 'rt.returnLoc': 'Lieu de retour',
  'rt.driverAge': 'Âge du conducteur', 'rt.passengers': 'Passagers',
  'rt.search': 'CHERCHER',
  'rt.trust1': 'Sans frais cachés', 'rt.trust2': 'Annulation gratuite', 'rt.trust3': 'Assistance 24/7',
  'rt.heroTitleA': 'Plus d’espace. Plus de souvenirs.', 'rt.heroTitleB': 'mieux ensemble.',
  'rt.sub': 'SUV, vans et 4x4 pour les familles, les amis et les longs trajets à travers le pays.',
  'rt.fleet.kicker': 'NOTRE FLOTTE', 'rt.fleet.title': 'Nos catégories de véhicules', 'rt.fleet.sub': 'Choisissez la taille adaptée à votre voyage.',
  'rt.fleet.from': 'à partir de', 'rt.fleet.cars': 'voitures', 'rt.fleet.view': 'VOIR LES VOITURES',
  'rt.safety.kicker': 'SÉCURITÉ & CONFORT', 'rt.safety.title': 'Sécurité et confort, toujours notre priorité',
  'rt.safety.1t': 'Assurance complète', 'rt.safety.1d': 'Couverture complète sur chaque location.',
  'rt.safety.2t': 'Assistance routière', 'rt.safety.2d': 'De l’aide partout dans le pays, jour et nuit.',
  'rt.safety.3t': 'Sièges enfants', 'rt.safety.3d': 'Sièges bébé et rehausseurs sur demande.',
  'rt.safety.4t': 'Kilométrage illimité', 'rt.safety.4d': 'Roulez aussi loin que votre voyage l’exige.',
  'rt.safety.5t': 'Intérieurs désinfectés', 'rt.safety.5d': 'Nettoyage complet entre chaque location.',
  'rt.pack.kicker': 'FORFAITS VOYAGE', 'rt.pack.title': 'Forfaits voyage populaires', 'rt.pack.sub': 'Des offres prêtes pour un week-end ou un tour complet.',
  'rt.pack.cta': 'VOIR LE FORFAIT',
  'rt.faq.kicker': 'FAQ', 'rt.faq.title': 'Questions fréquentes',
  'rt.app.kicker': 'VOTRE VOYAGE, SIMPLIFIÉ', 'rt.app.title': 'Réservez, prolongez ou annulez depuis votre téléphone',
  'rt.app.sub': 'Appelez l’agence pour le reste — nous répondons tous les jours.',
  'rt.app.call': 'Appelez-nous', 'rt.app.whatsapp': 'WhatsApp',
});
add('ar', {
  'ed.kicker': 'كراء السيارات',
  'ed.titleA': 'طريقة سهلة وسريعة', 'ed.titleB': 'لكراء سيارتك',
  'ed.sub': 'اختر من أسطول حديث، واحجز في دقيقتين، واستلم سيارتك حيث تصل.',
  'ed.cta': 'تصفح الأسطول',
  'ed.inventory.kicker': 'أحدث السيارات', 'ed.inventory.title': 'سيارات جاهزة للانطلاق',
  'ed.inventory.viewAll': 'عرض جميع السيارات',
  'ed.why.kicker': 'لماذا تختارنا', 'ed.why.title': 'كل ما تحتاجه لكراء سهل',
  'ed.why.1t': 'أسعار واضحة', 'ed.why.1d': 'السعر المعروض يشمل التأمين والمساعدة.',
  'ed.why.2t': 'سيارات مصونة', 'ed.why.2d': 'كل سيارة تُصان وتُنظف قبل التسليم.',
  'ed.why.3t': 'استلام سريع', 'ed.why.3d': 'عقدك جاهز عند وصولك.',
  'ed.why.4t': 'إلغاء مرن', 'ed.why.4d': 'الخطط تتغير — ألغِ مجاناً حتى 24 ساعة قبل الموعد.',
  'ed.num.kicker': 'أرقامنا',
  'ed.num.vehicles': 'سيارات في الأسطول', 'ed.num.categories': 'فئات السيارات', 'ed.num.locations': 'نقاط الاستلام',

  'rt.tab1': 'استلام وإرجاع', 'rt.tab2': 'ذهاب فقط', 'rt.tab3': 'نقل المطار',
  'rt.pickupLoc': 'مكان الاستلام', 'rt.returnLoc': 'مكان الإرجاع',
  'rt.driverAge': 'عمر السائق', 'rt.passengers': 'المسافرون',
  'rt.search': 'ابحث',
  'rt.trust1': 'بدون رسوم خفية', 'rt.trust2': 'إلغاء مجاني', 'rt.trust3': 'دعم على مدار الساعة',
  'rt.heroTitleA': 'مساحة أكبر. ذكريات أكثر.', 'rt.heroTitleB': 'أفضل معاً.',
  'rt.sub': 'سيارات دفع رباعي وشاحنات صغيرة للعائلات والأصدقاء والرحلات الطويلة.',
  'rt.fleet.kicker': 'أسطولنا', 'rt.fleet.title': 'فئات أسطولنا', 'rt.fleet.sub': 'اختر الحجم المناسب لرحلتك.',
  'rt.fleet.from': 'ابتداءً من', 'rt.fleet.cars': 'سيارات', 'rt.fleet.view': 'عرض السيارات',
  'rt.safety.kicker': 'الأمان والراحة', 'rt.safety.title': 'الأمان والراحة أولويتنا دائماً',
  'rt.safety.1t': 'تأمين شامل', 'rt.safety.1d': 'تغطية شاملة في كل كراء.',
  'rt.safety.2t': 'المساعدة على الطريق', 'rt.safety.2d': 'مساعدة في كل مكان، ليلاً ونهاراً.',
  'rt.safety.3t': 'مقاعد الأطفال', 'rt.safety.3d': 'مقاعد الأطفال عند الطلب.',
  'rt.safety.4t': 'كيلومترات غير محدودة', 'rt.safety.4d': 'قد إلى أبعد ما تريد.',
  'rt.safety.5t': 'تنظيف شامل', 'rt.safety.5d': 'تنظيف عميق بين كل كراء.',
  'rt.pack.kicker': 'عروض السفر', 'rt.pack.title': 'عروض سفر شائعة', 'rt.pack.sub': 'عروض جاهزة لعطلة نهاية الأسبوع أو جولة كاملة.',
  'rt.pack.cta': 'عرض الرحلة',
  'rt.faq.kicker': 'الأسئلة الشائعة', 'rt.faq.title': 'الأسئلة الشائعة',
  'rt.app.kicker': 'رحلتك، بأبسط طريقة', 'rt.app.title': 'احجز أو مدّد أو ألغِ من هاتفك',
  'rt.app.sub': 'اتصل بالوكالة لأي شيء آخر — نرد كل يوم.',
  'rt.app.call': 'اتصل بنا', 'rt.app.whatsapp': 'واتساب',
});
add('es', {
  'ed.kicker': 'ALQUILER DE COCHES',
  'ed.titleA': 'Fácil y rápido para', 'ed.titleB': 'alquilar tu coche',
  'ed.sub': 'Elige en una flota moderna, reserva en dos minutos y recoge el coche donde llegues.',
  'ed.cta': 'VER LA FLOTA',
  'ed.inventory.kicker': 'ÚLTIMAS INCORPORACIONES', 'ed.inventory.title': 'Coches listos para conducir',
  'ed.inventory.viewAll': 'VER TODOS LOS COCHES',
  'ed.why.kicker': 'POR QUÉ ELEGIRNOS', 'ed.why.title': 'Todo para un alquiler sencillo',
  'ed.why.1t': 'Precio transparente', 'ed.why.1d': 'El precio incluye seguro y asistencia.',
  'ed.why.2t': 'Coches mantenidos', 'ed.why.2d': 'Cada vehículo se revisa y limpia antes de la entrega.',
  'ed.why.3t': 'Recogida rápida', 'ed.why.3d': 'Tu contrato está listo al llegar.',
  'ed.why.4t': 'Cancelación flexible', 'ed.why.4d': 'Los planes cambian: cancela gratis hasta 24h antes.',
  'ed.num.kicker': 'NUESTROS NÚMEROS',
  'ed.num.vehicles': 'Coches en la flota', 'ed.num.categories': 'Categorías de vehículos', 'ed.num.locations': 'Puntos de recogida',

  'rt.tab1': 'Recogida y devolución', 'rt.tab2': 'Solo ida', 'rt.tab3': 'Traslado aeropuerto',
  'rt.pickupLoc': 'Lugar de recogida', 'rt.returnLoc': 'Lugar de devolución',
  'rt.driverAge': 'Edad del conductor', 'rt.passengers': 'Pasajeros',
  'rt.search': 'BUSCAR',
  'rt.trust1': 'Sin cargos ocultos', 'rt.trust2': 'Cancelación gratis', 'rt.trust3': 'Soporte 24/7',
  'rt.heroTitleA': 'Más espacio. Más recuerdos.', 'rt.heroTitleB': 'mejor juntos.',
  'rt.sub': 'SUVs, furgonetas y 4x4 para familias, amigos y grandes viajes por el país.',
  'rt.fleet.kicker': 'NUESTRA FLOTA', 'rt.fleet.title': 'Nuestras categorías de flota', 'rt.fleet.sub': 'Elige el tamaño que encaja con tu viaje.',
  'rt.fleet.from': 'desde', 'rt.fleet.cars': 'coches', 'rt.fleet.view': 'VER COCHES',
  'rt.safety.kicker': 'SEGURIDAD Y CONFORT', 'rt.safety.title': 'Seguridad y confort, siempre nuestra prioridad',
  'rt.safety.1t': 'Seguro completo', 'rt.safety.1d': 'Cobertura completa en cada alquiler.',
  'rt.safety.2t': 'Asistencia en carretera', 'rt.safety.2d': 'Ayuda en todo el país, de día y de noche.',
  'rt.safety.3t': 'Sillas infantiles', 'rt.safety.3d': 'Sillas de bebé y elevadores a petición.',
  'rt.safety.4t': 'Kilometraje ilimitado', 'rt.safety.4d': 'Conduce tan lejos como quieras.',
  'rt.safety.5t': 'Interiores desinfectados', 'rt.safety.5d': 'Limpieza profunda entre alquileres.',
  'rt.pack.kicker': 'PAQUETES DE VIAJE', 'rt.pack.title': 'Paquetes de viaje populares', 'rt.pack.sub': 'Ofertas listas para un fin de semana o una ruta completa.',
  'rt.pack.cta': 'VER PAQUETE',
  'rt.faq.kicker': 'PREGUNTAS', 'rt.faq.title': 'Preguntas frecuentes',
  'rt.app.kicker': 'TU VIAJE, MÁS FÁCIL', 'rt.app.title': 'Reserva, amplía o cancela desde el móvil',
  'rt.app.sub': 'Llama a la agencia para lo demás: respondemos todos los días.',
  'rt.app.call': 'Llámanos', 'rt.app.whatsapp': 'WhatsApp',
});


// ---------------- search bar ----------------
add('en', {
  'search.rent': 'Rent a Car', 'search.longterm': 'Long Term Rental', 'search.corporate': 'Corporate Rental',
  'search.pickupLoc': 'Pick-up Location', 'search.pickupDate': 'Pick-up Date', 'search.pickupTime': 'Pick-up Time',
  'search.dropDate': 'Drop-off Date', 'search.dropTime': 'Drop Time', 'search.cta': 'SEARCH CARS',
});
add('fr', {
  'search.rent': 'Louer une voiture', 'search.longterm': 'Location longue durée', 'search.corporate': 'Location pro',
  'search.pickupLoc': 'Lieu de retrait', 'search.pickupDate': 'Date de retrait', 'search.pickupTime': 'Heure de retrait',
  'search.dropDate': 'Date de retour', 'search.dropTime': 'Heure de retour', 'search.cta': 'CHERCHER',
});
add('ar', {
  'search.rent': 'استئجار سيارة', 'search.longterm': 'كراء طويل المدة', 'search.corporate': 'كراء للشركات',
  'search.pickupLoc': 'مكان الاستلام', 'search.pickupDate': 'تاريخ الاستلام', 'search.pickupTime': 'وقت الاستلام',
  'search.dropDate': 'تاريخ الإرجاع', 'search.dropTime': 'وقت الإرجاع', 'search.cta': 'ابحث',
});
add('es', {
  'search.rent': 'Alquilar un coche', 'search.longterm': 'Alquiler largo', 'search.corporate': 'Alquiler empresas',
  'search.pickupLoc': 'Lugar de recogida', 'search.pickupDate': 'Fecha de recogida', 'search.pickupTime': 'Hora de recogida',
  'search.dropDate': 'Fecha de devolución', 'search.dropTime': 'Hora de devolución', 'search.cta': 'BUSCAR',
});


// ---------------- popular / promo / newsletter ----------------
add('en', {
  'pop.kicker': 'POPULAR VEHICLES', 'pop.title': 'Choose Your Perfect Car', 'pop.viewAll': 'VIEW ALL VEHICLES',
  'pop.bookNow': 'BOOK NOW', 'pop.seats': 'Seats', 'pop.bags': 'Bags',
  'promo.badge': 'Special Offer', 'promo.titleA': 'Save More on', 'promo.titleB': 'Your Next Adventure',
  'promo.sub': 'Get up to 20% off on your first booking. Limited time offer! Use coupon code',
  'promo.at': 'at checkout.', 'promo.cta': 'GET 20% OFF',
  'promo.1t': 'No Hidden Fees', 'promo.1d': 'What you see is what you pay.',
  'promo.2t': 'Unlimited Miles', 'promo.2d': 'Drive freely with no extra charges.',
  'promo.3t': 'Full Insurance', 'promo.3d': 'All rentals include comprehensive insurance.',
  'promo.4t': 'Easy Booking', 'promo.4d': 'Book your car in just a few simple steps.',
  'news.title': 'Subscribe to Our Newsletter', 'news.sub': 'Get exclusive deals, travel tips, and updates.',
  'news.placeholder': 'Enter your email address', 'news.cta': 'SUBSCRIBE',
  'news.done': 'Thank you! You have been subscribed with 20% welcome discount.',
});
add('fr', {
  'pop.kicker': 'VÉHICULES POPULAIRES', 'pop.title': 'Choisissez Votre Voiture Idéale', 'pop.viewAll': 'VOIR TOUS LES VÉHICULES',
  'pop.bookNow': 'RÉSERVER', 'pop.seats': 'Places', 'pop.bags': 'Bagages',
  'promo.badge': 'Offre spéciale', 'promo.titleA': 'Économisez sur', 'promo.titleB': 'Votre prochaine aventure',
  'promo.sub': "Jusqu'à -20% sur votre première réservation. Offre limitée ! Utilisez le code",
  'promo.at': 'au paiement.', 'promo.cta': '-20% J’EN PROFITE',
  'promo.1t': 'Sans frais cachés', 'promo.1d': 'Ce que vous voyez est ce que vous payez.',
  'promo.2t': 'Kilométrage illimité', 'promo.2d': 'Roulez librement sans frais supplémentaires.',
  'promo.3t': 'Assurance complète', 'promo.3d': 'Toutes les locations incluent une assurance complète.',
  'promo.4t': 'Réservation facile', 'promo.4d': 'Réservez votre voiture en quelques étapes simples.',
  'news.title': 'Abonnez-vous à notre newsletter', 'news.sub': 'Recevez offres exclusives, conseils et nouveautés.',
  'news.placeholder': 'Entrez votre adresse e-mail', 'news.cta': 'S’ABONNER',
  'news.done': 'Merci ! Inscription confirmée avec -20% de bienvenue.',
});
add('ar', {
  'pop.kicker': 'السيارات الشائعة', 'pop.title': 'اختر سيارتك المثالية', 'pop.viewAll': 'عرض جميع السيارات',
  'pop.bookNow': 'احجز', 'pop.seats': 'مقاعد', 'pop.bags': 'حقائب',
  'promo.badge': 'عرض خاص', 'promo.titleA': 'وفّر أكثر في', 'promo.titleB': 'مغامرتك القادمة',
  'promo.sub': 'احصل على خصم حتى 20% على حجزك الأول. عرض محدود! استخدم الرمز',
  'promo.at': 'عند الدفع.', 'promo.cta': 'احصل على -20%',
  'promo.1t': 'بدون رسوم خفية', 'promo.1d': 'ما تراه هو ما تدفعه.',
  'promo.2t': 'كيلومترات غير محدودة', 'promo.2d': 'قد بحرية دون رسوم إضافية.',
  'promo.3t': 'تأمين شامل', 'promo.3d': 'جميع عمليات الكراء تشمل تأميناً شاملاً.',
  'promo.4t': 'حجز سهل', 'promo.4d': 'احجز سيارتك في خطوات بسيطة.',
  'news.title': 'اشترك في نشرتنا', 'news.sub': 'احصل على العروض الحصرية والنصائح والتحديثات.',
  'news.placeholder': 'أدخل بريدك الإلكتروني', 'news.cta': 'اشترك',
  'news.done': 'شكراً! تم الاشتراك مع خصم ترحيبي 20%.',
});
add('es', {
  'pop.kicker': 'VEHÍCULOS POPULARES', 'pop.title': 'Elige Tu Coche Perfecto', 'pop.viewAll': 'VER TODOS',
  'pop.bookNow': 'RESERVAR', 'pop.seats': 'Plazas', 'pop.bags': 'Maletas',
  'promo.badge': 'Oferta especial', 'promo.titleA': 'Ahorra más en', 'promo.titleB': 'Tu próxima aventura',
  'promo.sub': 'Hasta un 20% en tu primera reserva. ¡Oferta limitada! Usa el código',
  'promo.at': 'al pagar.', 'promo.cta': 'QUIERO -20%',
  'promo.1t': 'Sin cargos ocultos', 'promo.1d': 'Lo que ves es lo que pagas.',
  'promo.2t': 'Kilometraje ilimitado', 'promo.2d': 'Conduce libremente sin cargos extra.',
  'promo.3t': 'Seguro completo', 'promo.3d': 'Todos los alquileres incluyen seguro completo.',
  'promo.4t': 'Reserva fácil', 'promo.4d': 'Reserva tu coche en unos simples pasos.',
  'news.title': 'Suscríbete a nuestro boletín', 'news.sub': 'Recibe ofertas exclusivas, consejos y novedades.',
  'news.placeholder': 'Introduce tu correo', 'news.cta': 'SUSCRIBIRSE',
  'news.done': '¡Gracias! Suscripción confirmada con 20% de bienvenida.',
});


// ---------------- footer ----------------
add('en', {
  'foot.tagline': 'Your journey, our priority. Rent the perfect car and explore the world with fleetava Car Rental.',
  'foot.company': 'COMPANY', 'foot.about': 'About Us', 'foot.careers': 'Careers',
  'foot.terms': 'Terms & Conditions', 'foot.privacy': 'Privacy Policy', 'foot.contact': 'Contact Us',
  'foot.info': 'INFORMATION', 'foot.faq': 'FAQ', 'foot.guide': 'Rental Guide',
  'foot.insurance': 'Insurance', 'foot.payment': 'Payment Options', 'foot.blog': 'Blog',
  'foot.locations': 'TOP LOCATIONS', 'foot.contactUs': 'CONTACT US',
  'foot.rights': '© 2026 fleetava Car Rental. All rights reserved.', 'foot.slogan': 'We drive your journey.',
});
add('fr', {
  'foot.tagline': 'Votre voyage, notre priorité. Louez la voiture parfaite et explorez le monde avec fleetava.',
  'foot.company': 'ENTREPRISE', 'foot.about': 'À propos', 'foot.careers': 'Carrières',
  'foot.terms': 'CGV', 'foot.privacy': 'Confidentialité', 'foot.contact': 'Contact',
  'foot.info': 'INFORMATIONS', 'foot.faq': 'FAQ', 'foot.guide': 'Guide de location',
  'foot.insurance': 'Assurance', 'foot.payment': 'Moyens de paiement', 'foot.blog': 'Blog',
  'foot.locations': 'TOP AGENCES', 'foot.contactUs': 'CONTACT',
  'foot.rights': '© 2026 fleetava Location. Tous droits réservés.', 'foot.slogan': 'Nous conduisons votre voyage.',
});
add('ar', {
  'foot.tagline': 'رحلتك أولويتنا. استأجر السيارة المثالية واكتشف العالم مع fleetava.',
  'foot.company': 'الشركة', 'foot.about': 'من نحن', 'foot.careers': 'وظائف',
  'foot.terms': 'الشروط والأحكام', 'foot.privacy': 'الخصوصية', 'foot.contact': 'اتصل بنا',
  'foot.info': 'معلومات', 'foot.faq': 'أسئلة شائعة', 'foot.guide': 'دليل الكراء',
  'foot.insurance': 'التأمين', 'foot.payment': 'طرق الدفع', 'foot.blog': 'المدونة',
  'foot.locations': 'أهم الوكالات', 'foot.contactUs': 'اتصل بنا',
  'foot.rights': '© 2026 fleetava لكراء السيارات. جميع الحقوق محفوظة.', 'foot.slogan': 'نقود رحلتك.',
});
add('es', {
  'foot.tagline': 'Tu viaje, nuestra prioridad. Alquila el coche perfecto y explora el mundo con fleetava.',
  'foot.company': 'EMPRESA', 'foot.about': 'Nosotros', 'foot.careers': 'Empleo',
  'foot.terms': 'Términos', 'foot.privacy': 'Privacidad', 'foot.contact': 'Contacto',
  'foot.info': 'INFORMACIÓN', 'foot.faq': 'FAQ', 'foot.guide': 'Guía de alquiler',
  'foot.insurance': 'Seguro', 'foot.payment': 'Formas de pago', 'foot.blog': 'Blog',
  'foot.locations': 'TOP AGENCIAS', 'foot.contactUs': 'CONTACTO',
  'foot.rights': '© 2026 fleetava Alquiler. Todos los derechos reservados.', 'foot.slogan': 'Conducimos tu viaje.',
});

add('en', {
  'page.kicker': 'fleetava',
  'page.faqEmpty': 'This agency has not published questions yet.',
  'page.copyEmpty': 'This page has not been published yet.',
  'page.loading': 'Loading the car…',
  'page.missingCar': 'This car is not published.',
  'page.backToFleet': 'Back to the fleet',
});
add('fr', {
  'page.kicker': 'fleetava',
  'page.faqEmpty': 'Cette agence n’a pas encore publié de questions.',
  'page.copyEmpty': 'Cette page n’est pas encore publiée.',
  'page.loading': 'Chargement du véhicule…',
  'page.missingCar': 'Ce véhicule n’est pas publié.',
  'page.backToFleet': 'Retour à la flotte',
});
add('ar', {
  'page.kicker': 'fleetava',
  'page.faqEmpty': 'لم تنشر هذه الوكالة أسئلة بعد.',
  'page.copyEmpty': 'لم تُنشر هذه الصفحة بعد.',
  'page.loading': 'جارٍ تحميل السيارة…',
  'page.missingCar': 'هذه السيارة غير منشورة.',
  'page.backToFleet': 'العودة إلى الأسطول',
});
add('es', {
  'page.kicker': 'fleetava',
  'page.faqEmpty': 'Esta agencia aún no ha publicado preguntas.',
  'page.copyEmpty': 'Esta página aún no está publicada.',
  'page.loading': 'Cargando el coche…',
  'page.missingCar': 'Este coche no está publicado.',
  'page.backToFleet': 'Volver a la flota',
});


// ---------------- catalog ----------------
add('en', {
  'cat.home': 'Home', 'cat.fleet': 'Our Vehicle Fleet', 'cat.title': 'Explore Available Vehicles',
  'cat.showing': 'vehicles available for immediate reservation', 'cat.of': 'of',
  'cat.filter': 'Filter Fleet', 'cat.active': '(Active)', 'cat.all': 'All',
  'cat.searchFilters': 'Search Filters', 'cat.reset': 'Reset', 'cat.keyword': 'Keyword Search',
  'cat.keywordPh': 'Search model, make...', 'cat.maxRate': 'Max Daily Rate', 'cat.transmission': 'Transmission',
  'cat.seats': 'Passenger Capacity', 'cat.any': 'Any', 'cat.fuel': 'Powertrain / Fuel',
  'cat.sort': 'Sort Order', 'cat.sortRec': 'Featured / Recommended', 'cat.sortAsc': 'Price: Low to High',
  'cat.sortDesc': 'Price: High to Low', 'cat.sortRating': 'Customer Rating',
  'cat.empty': 'No vehicles found matching filters',
  'cat.emptyHint': 'Try adjusting your price range, fuel type, or clear the search keywords to see available rental fleet.',
  'cat.resetAll': 'Reset All Filters', 'cat.details': 'Details', 'cat.bookNow': 'BOOK NOW',
  'cat.seatsU': 'Seats', 'cat.bagsU': 'Bags',
});
add('fr', {
  'cat.home': 'Accueil', 'cat.fleet': 'Notre flotte', 'cat.title': 'Explorez nos véhicules disponibles',
  'cat.showing': 'véhicules disponibles à la réservation immédiate', 'cat.of': 'sur',
  'cat.filter': 'Filtrer', 'cat.active': '(Actif)', 'cat.all': 'Tous',
  'cat.searchFilters': 'Filtres de recherche', 'cat.reset': 'Réinitialiser', 'cat.keyword': 'Mot-clé',
  'cat.keywordPh': 'Modèle, marque...', 'cat.maxRate': 'Tarif max / jour', 'cat.transmission': 'Boîte de vitesse',
  'cat.seats': 'Places passagers', 'cat.any': 'Tous', 'cat.fuel': 'Motorisation',
  'cat.sort': "Ordre de tri", 'cat.sortRec': 'Recommandés', 'cat.sortAsc': 'Prix croissant',
  'cat.sortDesc': 'Prix décroissant', 'cat.sortRating': 'Mieux notés',
  'cat.empty': 'Aucun véhicule trouvé',
  'cat.emptyHint': 'Ajustez le prix, le carburant ou effacez la recherche pour voir la flotte.',
  'cat.resetAll': 'Tout réinitialiser', 'cat.details': 'Détails', 'cat.bookNow': 'RÉSERVER',
  'cat.seatsU': 'Places', 'cat.bagsU': 'Bagages',
});
add('ar', {
  'cat.home': 'الرئيسية', 'cat.fleet': 'أسطولنا', 'cat.title': 'اكتشف السيارات المتاحة',
  'cat.showing': 'سيارة متاحة للحجز الفوري', 'cat.of': 'من',
  'cat.filter': 'تصفية', 'cat.active': '(نشط)', 'cat.all': 'الكل',
  'cat.searchFilters': 'فلاتر البحث', 'cat.reset': 'إعادة تعيين', 'cat.keyword': 'بحث',
  'cat.keywordPh': 'الموديل، الماركة...', 'cat.maxRate': 'السعر الأقصى / يوم', 'cat.transmission': 'ناقل الحركة',
  'cat.seats': 'عدد المقاعد', 'cat.any': 'الكل', 'cat.fuel': 'الوقود',
  'cat.sort': 'الترتيب', 'cat.sortRec': 'المقترحة', 'cat.sortAsc': 'السعر: من الأقل',
  'cat.sortDesc': 'السعر: من الأعلى', 'cat.sortRating': 'الأعلى تقييماً',
  'cat.empty': 'لا توجد سيارات مطابقة',
  'cat.emptyHint': 'جرّب تعديل السعر أو الوقود أو امسح البحث لرؤية الأسطول.',
  'cat.resetAll': 'إعادة تعيين الكل', 'cat.details': 'التفاصيل', 'cat.bookNow': 'احجز',
  'cat.seatsU': 'مقاعد', 'cat.bagsU': 'حقائب',
});
add('es', {
  'cat.home': 'Inicio', 'cat.fleet': 'Nuestra flota', 'cat.title': 'Explora los vehículos disponibles',
  'cat.showing': 'vehículos disponibles para reserva inmediata', 'cat.of': 'de',
  'cat.filter': 'Filtrar', 'cat.active': '(Activo)', 'cat.all': 'Todos',
  'cat.searchFilters': 'Filtros', 'cat.reset': 'Restablecer', 'cat.keyword': 'Palabra clave',
  'cat.keywordPh': 'Modelo, marca...', 'cat.maxRate': 'Tarifa máx. / día', 'cat.transmission': 'Transmisión',
  'cat.seats': 'Plazas', 'cat.any': 'Todas', 'cat.fuel': 'Combustible',
  'cat.sort': 'Orden', 'cat.sortRec': 'Recomendados', 'cat.sortAsc': 'Precio: menor a mayor',
  'cat.sortDesc': 'Precio: mayor a menor', 'cat.sortRating': 'Mejor valorados',
  'cat.empty': 'Sin vehículos con esos filtros',
  'cat.emptyHint': 'Ajusta el precio, el combustible o borra la búsqueda para ver la flota.',
  'cat.resetAll': 'Restablecer todo', 'cat.details': 'Detalles', 'cat.bookNow': 'RESERVAR',
  'cat.seatsU': 'Plazas', 'cat.bagsU': 'Maletas',
});


// ---------------- vehicle detail ----------------
add('en', {
  'det.back': 'Back to Fleet', 'det.home': 'Home', 'det.vehicles': 'Vehicles',
  'det.share': 'Share', 'det.copied': 'Link Copied!', 'det.saved': 'Saved', 'det.save': 'Save',
  'det.class': 'CLASS', 'det.model': 'MODEL', 'det.reviews': 'reviews',
  'det.rate': 'Daily Rental Rate', 'det.available': 'Available for Fast Curbside Pickup',
  'det.capacity': 'Capacity', 'det.seatsU': 'Seats', 'det.transmission': 'Transmission',
  'det.luggage': 'Luggage Space', 'det.bagsU': 'Large Bags', 'det.powertrain': 'Powertrain',
  'det.overview': 'Vehicle Overview & Highlights',
  'det.narrA': 'The', 'det.narrB': 'delivers exceptional driving comfort, advanced handling, and top-tier safety. Whether navigating downtown city traffic or taking on scenic mountain highways, this',
  'det.narrC': 'offers an uncompromising blend of performance, fuel efficiency',
  'det.narrD': 'and luxury ergonomics.',
  'det.specs': 'Full Technical Specifications', 'det.engine': 'Engine / Drivetrain',
  'det.economy': 'Fuel Economy / Range', 'det.mileage': 'Vehicle Odometer Mileage',
  'det.seating': 'Seating Configuration', 'det.fullSeats': 'Full-Size Seats',
  'det.drive': 'Drive Type', 'det.driveV': 'Front / All-Wheel Drive',
  'det.airbag': 'Airbag Safety Rating', 'det.features': 'Standard Features & Cabin Equipment',
  'det.unlimited': 'Unlimited Miles Included', 'det.clean': 'Clean & Sanitized Guarantee', 'det.fuel': 'Full Fuel Tank at Handoff',
  'det.req': 'Rental Requirements & Terms', 'det.age': 'Driver Age & License',
  'det.ageD': 'Drivers must be 21+ years old with a valid driving license held for at least 1 year. No young driver surcharges for 25+.',
  'det.deposit': 'Security Deposit',
  'det.depositD': 'Refundable authorization hold of 2,000 DH placed on credit card upon key pickup, released upon return.',
  'det.cancel': 'Free 100% Cancellation',
  'det.cancelD': 'Modify dates or cancel anytime up to 1 hour before pickup with immediate full refund.',
  'det.fuelP': 'Fuel Return Policy',
  'det.fuelPD': 'Vehicle is provided full of fuel and should be returned with the same fuel level.',
  'det.revT': 'Customer Experience & Reviews', 'det.revSub': 'based on verified fleetava renters',
  'det.outOf': 'out of 5', 'det.verified': 'Verified Renter',
  'det.rev1': 'Picked up the car at Agadir Airport. Flawless process, car smelled brand new and drove like a dream. Highly recommend fleetava!',
  'det.rev2': 'Super clean, great mileage, and seamless key return. The booking took less than 2 minutes and no hidden fees were charged.',
  'det.best': 'Best Rate Guaranteed', 'det.pickLoc': 'Pick-up Location', 'det.pickDate': 'Pick-up Date',
  'det.retDate': 'Return Date', 'det.days': 'Rental Days', 'det.unlimM': 'Unlimited Mileage',
  'det.free': 'FREE', 'det.liab': 'Mandatory Liability Cover', 'det.included': 'INCLUDED',
  'det.total': 'Estimated Total', 'det.reserve': 'RESERVE THIS', 'det.cancelHr': 'Free 100% Cancellation up to 1 hr before pickup',
  'det.noCharge': 'No credit card charge until confirmation', 'det.roadside': '24/7 Roadside Assistance helpline',
  'det.more': 'MORE OPTIONS', 'det.similar': 'Similar Vehicles in Our Fleet', 'det.viewFleet': 'View Full Fleet',
  'det.viewDetails': 'View Details →', 'det.exterior': 'Exterior', 'det.license': 'License',
});
add('fr', {
  'det.back': 'Retour à la flotte', 'det.home': 'Accueil', 'det.vehicles': 'Véhicules',
  'det.share': 'Partager', 'det.copied': 'Lien copié !', 'det.saved': 'Sauvegardé', 'det.save': 'Sauver',
  'det.class': 'CATÉGORIE', 'det.model': 'MODÈLE', 'det.reviews': 'avis',
  'det.rate': 'Tarif journalier', 'det.available': 'Disponible — remise rapide',
  'det.capacity': 'Capacité', 'det.seatsU': 'Places', 'det.transmission': 'Boîte de vitesse',
  'det.luggage': 'Coffre', 'det.bagsU': 'Grands bagages', 'det.powertrain': 'Motorisation',
  'det.overview': 'Aperçu & Points forts',
  'det.narrA': 'La', 'det.narrB': 'offre un confort de conduite exceptionnel, une tenue de route avancée et une sécurité de premier ordre. En ville comme sur les routes de montagne, cette',
  'det.narrC': 'allie performance, sobriété',
  'det.narrD': 'et ergonomie soignée.',
  'det.specs': 'Fiche technique complète', 'det.engine': 'Moteur / Transmission',
  'det.economy': 'Consommation', 'det.mileage': 'Kilométrage compteur',
  'det.seating': 'Places assises', 'det.fullSeats': 'Places entières',
  'det.drive': 'Transmission', 'det.driveV': 'Traction / Intégrale',
  'det.airbag': 'Sécurité airbags', 'det.features': 'Équipements de série',
  'det.unlimited': 'Kilométrage illimité inclus', 'det.clean': 'Propre & désinfecté garanti', 'det.fuel': 'Plein de carburant à la remise',
  'det.req': 'Conditions de location', 'det.age': 'Âge & Permis',
  'det.ageD': "Conducteur 21 ans et + avec permis valide depuis au moins 1 an. Pas de surcharge jeune conducteur dès 25 ans.",
  'det.deposit': 'Caution',
  'det.depositD': "Empreinte de 2 000 DH sur carte bancaire à la remise des clés, restituée au retour.",
  'det.cancel': 'Annulation 100% gratuite',
  'det.cancelD': "Modifiez ou annulez jusqu'à 1 h avant le retrait avec remboursement immédiat.",
  'det.fuelP': 'Politique carburant',
  'det.fuelPD': 'Véhicule remis avec le plein, à restituer au même niveau.',
  'det.revT': 'Avis clients vérifiés', 'det.revSub': 'sur la base des locataires fleetava vérifiés',
  'det.outOf': 'sur 5', 'det.verified': 'Locataire vérifié',
  'det.rev1': "Prise en main à l'aéroport d'Agadir. Processus impeccable, voiture comme neuve. Je recommande fleetava !",
  'det.rev2': 'Très propre, kilométrage raisonnable, restitution des clés sans friction. Réservation en moins de 2 minutes, sans frais cachés.',
  'det.best': 'Meilleur tarif garanti', 'det.pickLoc': 'Lieu de retrait', 'det.pickDate': 'Date de retrait',
  'det.retDate': 'Date de retour', 'det.days': 'Jours de location', 'det.unlimM': 'Kilométrage illimité',
  'det.free': 'GRATUIT', 'det.liab': 'Responsabilité civile incluse', 'det.included': 'INCLUS',
  'det.total': 'Total estimé', 'det.reserve': 'RÉSERVER CETTE', 'det.cancelHr': "Annulation gratuite jusqu'à 1 h avant le retrait",
  'det.noCharge': 'Aucun débit avant confirmation', 'det.roadside': 'Assistance dépannage 24/7',
  'det.more': "PLUS D'OPTIONS", 'det.similar': 'Véhicules similaires', 'det.viewFleet': 'Voir toute la flotte',
  'det.viewDetails': 'Voir détails →', 'det.exterior': 'Extérieur', 'det.license': 'Immat.',
});
add('ar', {
  'det.back': 'عودة إلى الأسطول', 'det.home': 'الرئيسية', 'det.vehicles': 'السيارات',
  'det.share': 'مشاركة', 'det.copied': 'تم نسخ الرابط!', 'det.saved': 'محفوظة', 'det.save': 'حفظ',
  'det.class': 'الفئة', 'det.model': 'الموديل', 'det.reviews': 'تقييم',
  'det.rate': 'السعر اليومي', 'det.available': 'متاحة للاستلام السريع',
  'det.capacity': 'السعة', 'det.seatsU': 'مقاعد', 'det.transmission': 'ناقل الحركة',
  'det.luggage': 'الأمتعة', 'det.bagsU': 'حقائب كبيرة', 'det.powertrain': 'المحرك',
  'det.overview': 'نظرة عامة ومميزات',
  'det.narrA': 'تقدم', 'det.narrB': 'راحة قيادة استثنائية وثباتاً متقدماً وسلامة من الدرجة الأولى. في المدينة أو على الطرق الجبلية، هذه',
  'det.narrC': 'تجمع بين الأداء والاقتصاد في الوقود',
  'det.narrD': 'وراحة فاخرة.',
  'det.specs': 'المواصفات التقنية الكاملة', 'det.engine': 'المحرك',
  'det.economy': 'الاستهلاك', 'det.mileage': 'عداد الكيلومترات',
  'det.seating': 'المقاعد', 'det.fullSeats': 'مقاعد كاملة',
  'det.drive': 'الدفع', 'det.driveV': 'أمامي / رباعي',
  'det.airbag': 'الوسائد الهوائية', 'det.features': 'التجهيزات الأساسية',
  'det.unlimited': 'كيلومترات غير محدودة', 'det.clean': 'نظيفة ومعقمة', 'det.fuel': 'خزان ممتلئ عند التسليم',
  'det.req': 'شروط الكراء', 'det.age': 'السن ورخصة السياقة',
  'det.ageD': 'يجب أن يكون السائق 21 سنة فأكثر برخصة سارية منذ سنة على الأقل.',
  'det.deposit': 'الضمان',
  'det.depositD': 'حجز 2000 درهم على البطاقة البنكية عند التسليم، يُسترجع عند الإرجاع.',
  'det.cancel': 'إلغاء مجاني 100%',
  'det.cancelD': 'عدّل أو ألغِ حتى ساعة قبل الاستلام مع استرداد فوري.',
  'det.fuelP': 'سياسة الوقود',
  'det.fuelPD': 'تُسلَّم السيارة بخزان ممتلئ ويجب إرجاعها بنفس المستوى.',
  'det.revT': 'آراء الزبناء', 'det.revSub': 'بناءً على مستأجرين موثقين لدى fleetava',
  'det.outOf': 'من 5', 'det.verified': 'مستأجر موثق',
  'det.rev1': 'استلمت السيارة في مطار أكادير. إجراءات مثالية وسيارة كالجديدة. أنصح بـ fleetava!',
  'det.rev2': 'نظيفة جداً وإرجاع سلس للمفاتيح. الحجز استغرق أقل من دقيقتين وبدون رسوم خفية.',
  'det.best': 'ضمان أفضل سعر', 'det.pickLoc': 'مكان الاستلام', 'det.pickDate': 'تاريخ الاستلام',
  'det.retDate': 'تاريخ الإرجاع', 'det.days': 'أيام الكراء', 'det.unlimM': 'كيلومترات غير محدودة',
  'det.free': 'مجاناً', 'det.liab': 'تغطية المسؤولية مشمولة', 'det.included': 'مشمول',
  'det.total': 'المجموع التقديري', 'det.reserve': 'احجز هذه', 'det.cancelHr': 'إلغاء مجاني حتى ساعة قبل الاستلام',
  'det.noCharge': 'لا خصم قبل التأكيد', 'det.roadside': 'مساعدة على الطريق 24/7',
  'det.more': 'خيارات أخرى', 'det.similar': 'سيارات مشابهة في أسطولنا', 'det.viewFleet': 'عرض الأسطول كاملاً',
  'det.viewDetails': 'عرض التفاصيل ←', 'det.exterior': 'اللون', 'det.license': 'الترقيم',
});
add('es', {
  'det.back': 'Volver a la flota', 'det.home': 'Inicio', 'det.vehicles': 'Vehículos',
  'det.share': 'Compartir', 'det.copied': '¡Enlace copiado!', 'det.saved': 'Guardado', 'det.save': 'Guardar',
  'det.class': 'CLASE', 'det.model': 'MODELO', 'det.reviews': 'reseñas',
  'det.rate': 'Tarifa diaria', 'det.available': 'Disponible para entrega rápida',
  'det.capacity': 'Capacidad', 'det.seatsU': 'Plazas', 'det.transmission': 'Transmisión',
  'det.luggage': 'Maletero', 'det.bagsU': 'Maletas grandes', 'det.powertrain': 'Motor',
  'det.overview': 'Resumen y destacados',
  'det.narrA': 'El', 'det.narrB': 'ofrece un confort excepcional, gran manejo y seguridad de primer nivel. En ciudad o en carreteras de montaña, este',
  'det.narrC': 'combina rendimiento, bajo consumo',
  'det.narrD': 'y ergonomía de lujo.',
  'det.specs': 'Ficha técnica completa', 'det.engine': 'Motor / Transmisión',
  'det.economy': 'Consumo', 'det.mileage': 'Kilometraje',
  'det.seating': 'Plazas', 'det.fullSeats': 'Plazas completas',
  'det.drive': 'Tracción', 'det.driveV': 'Delantera / Total',
  'det.airbag': 'Airbags', 'det.features': 'Equipamiento de serie',
  'det.unlimited': 'Kilometraje ilimitado incluido', 'det.clean': 'Limpio y desinfectado', 'det.fuel': 'Depósito lleno a la entrega',
  'det.req': 'Requisitos del alquiler', 'det.age': 'Edad y carnet',
  'det.ageD': 'Conductor de 21+ años con carnet válido de al menos 1 año. Sin recargo joven desde los 25.',
  'det.deposit': 'Fianza',
  'det.depositD': 'Retención reembolsable de 2.000 DH en tarjeta a la entrega, liberada al devolver.',
  'det.cancel': 'Cancelación 100% gratis',
  'det.cancelD': 'Modifica o cancela hasta 1 h antes con reembolso inmediato.',
  'det.fuelP': 'Política de combustible',
  'det.fuelPD': 'Se entrega con el depósito lleno y debe devolverse igual.',
  'det.revT': 'Opiniones verificadas', 'det.revSub': 'según arrendatarios verificados de fleetava',
  'det.outOf': 'de 5', 'det.verified': 'Arrendatario verificado',
  'det.rev1': 'Recogí el coche en el aeropuerto de Agadir. Proceso impecable, coche como nuevo. ¡Recomiendo fleetava!',
  'det.rev2': 'Limpísimo, buen kilometraje y devolución sin fricciones. Reserva en menos de 2 minutos y sin cargos ocultos.',
  'det.best': 'Mejor precio garantizado', 'det.pickLoc': 'Lugar de recogida', 'det.pickDate': 'Fecha de recogida',
  'det.retDate': 'Fecha de devolución', 'det.days': 'Días de alquiler', 'det.unlimM': 'Kilometraje ilimitado',
  'det.free': 'GRATIS', 'det.liab': 'Cobertura incluida', 'det.included': 'INCLUIDO',
  'det.total': 'Total estimado', 'det.reserve': 'RESERVAR ESTE', 'det.cancelHr': 'Cancelación gratis hasta 1 h antes',
  'det.noCharge': 'Sin cargo hasta la confirmación', 'det.roadside': 'Asistencia 24/7',
  'det.more': 'MÁS OPCIONES', 'det.similar': 'Vehículos similares', 'det.viewFleet': 'Ver toda la flota',
  'det.viewDetails': 'Ver detalles →', 'det.exterior': 'Color', 'det.license': 'Matrícula',
});


// ---------------- vehicle modal ----------------
add('en', {
  'vm.class': 'Class', 'vm.capacity': 'Capacity', 'vm.passengers': 'Passengers',
  'vm.gearbox': 'Gearbox', 'vm.baggage': 'Baggage', 'vm.bagsU': 'Large Bags',
  'vm.fuel': 'Fuel & Economy', 'vm.features': 'Included Premium Features & Amenities',
  'vm.unlimited': 'Unlimited Free Mileage', 'vm.ac': 'Air Conditioning & Climate Control',
  'vm.guarantee': 'fleetava Clean & Ready Guarantee',
  'vm.guaranteeD': 'Every vehicle is thoroughly inspected, sanitized, and fully fueled before handoff. Free 100% cancellation up to 1 hour before pickup.',
  'vm.taxes': 'Taxes & basic liability included', 'vm.close': 'Close', 'vm.book': 'BOOK THIS CAR',
});
add('fr', {
  'vm.class': 'Catégorie', 'vm.capacity': 'Capacité', 'vm.passengers': 'Passagers',
  'vm.gearbox': 'Boîte de vitesse', 'vm.baggage': 'Bagages', 'vm.bagsU': 'Grands bagages',
  'vm.fuel': 'Carburant', 'vm.features': 'Équipements premium inclus',
  'vm.unlimited': 'Kilométrage illimité', 'vm.ac': 'Climatisation',
  'vm.guarantee': 'Garantie fleetava Propre & Prêt',
  'vm.guaranteeD': "Chaque véhicule est inspecté, désinfecté et remis avec le plein. Annulation 100% gratuite jusqu'à 1 h avant le retrait.",
  'vm.taxes': 'Taxes et responsabilité civile incluses', 'vm.close': 'Fermer', 'vm.book': 'RÉSERVER',
});
add('ar', {
  'vm.class': 'الفئة', 'vm.capacity': 'السعة', 'vm.passengers': 'ركاب',
  'vm.gearbox': 'ناقل الحركة', 'vm.baggage': 'الأمتعة', 'vm.bagsU': 'حقائب كبيرة',
  'vm.fuel': 'الوقود', 'vm.features': 'التجهيزات المشمولة',
  'vm.unlimited': 'كيلومترات غير محدودة', 'vm.ac': 'مكيف الهواء',
  'vm.guarantee': 'ضمان fleetava للنظافة والجاهزية',
  'vm.guaranteeD': 'تُفحص كل سيارة وتُعقم وتُسلَّم بخزان ممتلئ. إلغاء مجاني حتى ساعة قبل الاستلام.',
  'vm.taxes': 'الضرائب والمسؤولية المدنية مشمولة', 'vm.close': 'إغلاق', 'vm.book': 'احجز هذه السيارة',
});
add('es', {
  'vm.class': 'Clase', 'vm.capacity': 'Capacidad', 'vm.passengers': 'Pasajeros',
  'vm.gearbox': 'Cambio', 'vm.baggage': 'Equipaje', 'vm.bagsU': 'Maletas grandes',
  'vm.fuel': 'Combustible', 'vm.features': 'Extras premium incluidos',
  'vm.unlimited': 'Kilometraje ilimitado', 'vm.ac': 'Aire acondicionado',
  'vm.guarantee': 'Garantía fleetava Limpio y Listo',
  'vm.guaranteeD': 'Cada coche se inspecciona, desinfecta y entrega con el depósito lleno. Cancelación gratis hasta 1 h antes.',
  'vm.taxes': 'Impuestos y responsabilidad incluidos', 'vm.close': 'Cerrar', 'vm.book': 'RESERVAR ESTE COCHE',
});


// ---------------- profile ----------------
add('en', {
  'prof.gold': 'Gold Member', 'prof.license': 'Driver License',
  'prof.points': 'Loyalty Points', 'prof.verified': 'Verified Driver',
  'prof.reward': 'Reward Status', 'prof.freeDay': '1 Free Rental Day Earned',
  'prof.freeDaySub': 'Applies on your next weekend reservation',
  'prof.tabActive': 'Active Reservations', 'prof.tabPast': 'Past Trips',
  'prof.tabFav': 'Saved Cars', 'prof.tabSettings': 'Driver Preferences',
  'prof.emptyActive': 'No active reservations right now',
  'prof.emptyActiveD': 'Ready for your next journey? Browse our available fleet and book with 100% free cancellation.',
  'prof.browse': 'Browse Fleet', 'prof.totalPaid': 'Total Paid',
  'prof.modify': 'Modify Dates', 'prof.cancel': 'Cancel',
  'prof.emptyPast': 'No past rental trips recorded yet.',
  'prof.invoice': 'Download Invoice', 'prof.receipt': 'Receipt downloaded for',
  'prof.emptyFav': 'No saved vehicles yet',
  'prof.emptyFavD': 'Click the heart icon on any vehicle in the fleet catalog to bookmark it here for quick reservations.',
  'prof.viewDetails': 'View Details', 'prof.rentNow': 'Rent Now',
  'prof.settingsT': 'Driver Profile & Notification Preferences',
  'prof.name': 'Full Legal Name', 'prof.email': 'Primary Email', 'prof.phone': 'Phone (SMS Updates)',
  'prof.dl': 'Driver License Number', 'prof.save': 'Save Profile Changes', 'prof.saved': 'Profile settings updated successfully!',
  'prof.cancelT': 'Cancel Reservation', 'prof.cancelD': 'you will receive a 100% full refund immediately with no penalties.',
  'prof.keep': 'Keep Reservation', 'prof.confirmCancel': 'Confirm Free Cancellation',
  'prof.modT': 'Modify Dates for', 'prof.modD': 'Select your new pickup and drop-off dates.',
  'prof.newPickup': 'New Pick-up Date', 'prof.newReturn': 'New Return Date',
  'prof.cancelBtn': 'Cancel', 'prof.saveDates': 'Save New Dates',
  'prof.retLabel': 'Return:', 'prof.freeCancelPol': 'Free Cancellation Policy',
  'prof.signIn': 'Sign in', 'prof.register': 'Create account', 'prof.signOut': 'Sign out',
  'prof.password': 'Password (12+ characters)', 'prof.accountHint': 'Sign in to see the reservations stored for your email.',
  'prof.firstName': 'First name', 'prof.lastName': 'Last name', 'prof.code': 'SMS code',
  'prof.codeHint': 'Enter the code sent to', 'prof.demoCode': 'Demo code', 'prof.resend': 'Send a new code',
  'prof.verify': 'Confirm phone', 'prof.locale': 'Language', 'prof.avatar': 'Profile photo',
  'prof.showPassword': 'Show', 'prof.hidePassword': 'Hide', 'prof.invitePassword': 'Password',
  'prof.acceptInvite': 'Accept invitation', 'prof.inviteExpired': 'This invitation is no longer valid.',
  'prof.confirmEmail': 'Confirm email',
  'prof.verifyEmail': 'Verify email', 'prof.emailVerified': 'Email verified', 'prof.emailUnverified': 'Email not verified',
  'prof.emailSent': 'Verification link sent. The address does not change.',
  'prof.currentPassword': 'Current password', 'prof.newPassword': 'New password (12+ characters)',
  'prof.confirmPassword': 'Confirm new password', 'prof.passwordMismatch': 'The new passwords do not match.',
  'prof.changePassword': 'Change password', 'prof.passwordSaved': 'Password updated.',
  'prof.remember': 'Remember me', 'prof.forgot': 'Forgot password?', 'prof.sendCode': 'Send code',
  'prof.resetPassword': 'Reset password', 'prof.emailCode': 'Email code',
  'prof.emailCodeSent': 'If this email belongs to an account, a code is on its way. The address does not change.',
  'prof.passwordReset': 'Password updated. Sign in with the new password.',
  'prof.emailHint': 'This address cannot be changed. A code confirms you can open it.',
  'prof.emailReady': 'A code was sent to your email. The address does not change.',
  'prof.confirmCode': 'Verify code',
  'prof.passwordHint': 'At least 12 characters. Changing it does not turn off other checks.',
  'prof.phoneHint': 'An SMS code confirms the number before it is saved.',
  'prof.forgotHint': 'Forgot the current password? Use Forgot password on the sign-in form. It emails a code to this address.',
  'prof.authImage': 'Two travelers beside a rental car on a coastal road',
  'prof.authBriefing': 'Briefing',
  'prof.authEyebrow': 'Renter',
  'prof.authTitle': 'Your reservations',
  'prof.authLead': 'Sign in with the email you used for this agency. A new account confirms a Moroccan phone number before it opens.',
  'prof.authPointBookings': 'Active and past trips stay on this site.',
  'prof.authPointPhone': 'Registration asks for a phone code before the account opens.',
  'prof.authStaff': 'Agency owner or front desk? Sign in on the desk.',
  'prof.authWorking': 'Signing in…',
  'prof.authCreating': 'Creating account…',
});
add('fr', {
  'prof.gold': 'Membre Gold', 'prof.license': 'Permis de conduire',
  'prof.points': 'Points de fidélité', 'prof.verified': 'Conducteur vérifié',
  'prof.reward': 'Récompenses', 'prof.freeDay': '1 journée offerte gagnée',
  'prof.freeDaySub': "Valable sur votre prochaine réservation week-end",
  'prof.tabActive': 'Réservations actives', 'prof.tabPast': 'Voyages passés',
  'prof.tabFav': 'Favoris', 'prof.tabSettings': 'Préférences',
  'prof.emptyActive': 'Aucune réservation active',
  'prof.emptyActiveD': 'Prêt pour votre prochain voyage ? Parcourez la flotte avec annulation 100% gratuite.',
  'prof.browse': 'Voir la flotte', 'prof.totalPaid': 'Total payé',
  'prof.modify': 'Modifier', 'prof.cancel': 'Annuler',
  'prof.emptyPast': 'Aucun voyage passé enregistré.',
  'prof.invoice': 'Télécharger la facture', 'prof.receipt': 'Facture téléchargée pour',
  'prof.emptyFav': 'Aucun favori',
  'prof.emptyFavD': "Cliquez sur le cœur d'un véhicule pour le retrouver ici.",
  'prof.viewDetails': 'Détails', 'prof.rentNow': 'Louer',
  'prof.settingsT': 'Profil conducteur & notifications',
  'prof.name': 'Nom légal complet', 'prof.email': 'E-mail principal', 'prof.phone': 'Téléphone (SMS)',
  'prof.dl': 'N° de permis', 'prof.save': 'Enregistrer', 'prof.saved': 'Profil mis à jour !',
  'prof.cancelT': 'Annuler la réservation', 'prof.cancelD': 'vous recevrez un remboursement intégral immédiat sans pénalités.',
  'prof.keep': 'Garder', 'prof.confirmCancel': "Confirmer l'annulation",
  'prof.modT': 'Modifier les dates de', 'prof.modD': 'Choisissez vos nouvelles dates de retrait et retour.',
  'prof.newPickup': 'Nouvelle date de retrait', 'prof.newReturn': 'Nouvelle date de retour',
  'prof.cancelBtn': 'Annuler', 'prof.saveDates': 'Enregistrer',
  'prof.retLabel': 'Retour :', 'prof.freeCancelPol': "Politique d'annulation gratuite",
  'prof.signIn': 'Connexion', 'prof.register': 'Créer un compte', 'prof.signOut': 'Déconnexion',
  'prof.password': 'Mot de passe (12 caractères min.)', 'prof.accountHint': 'Connectez-vous pour voir les réservations liées à votre e-mail.',
  'prof.firstName': 'Prénom', 'prof.lastName': 'Nom', 'prof.code': 'Code SMS',
  'prof.codeHint': 'Saisissez le code envoyé au', 'prof.demoCode': 'Code démo', 'prof.resend': 'Renvoyer le code',
  'prof.verify': 'Confirmer le téléphone', 'prof.locale': 'Langue', 'prof.avatar': 'Photo de profil',
  'prof.showPassword': 'Afficher', 'prof.hidePassword': 'Masquer', 'prof.invitePassword': 'Mot de passe',
  'prof.acceptInvite': 'Accepter l’invitation', 'prof.inviteExpired': 'Cette invitation n’est plus valable.',
  'prof.confirmEmail': 'Confirmer l’e-mail',
  'prof.verifyEmail': 'Vérifier l’e-mail', 'prof.emailVerified': 'E-mail vérifié', 'prof.emailUnverified': 'E-mail non vérifié',
  'prof.emailSent': 'Lien de vérification envoyé. L’adresse ne change pas.',
  'prof.currentPassword': 'Mot de passe actuel', 'prof.newPassword': 'Nouveau mot de passe (12 caractères)',
  'prof.confirmPassword': 'Confirmer le nouveau mot de passe', 'prof.passwordMismatch': 'Les nouveaux mots de passe ne correspondent pas.',
  'prof.changePassword': 'Changer le mot de passe', 'prof.passwordSaved': 'Mot de passe mis à jour.',
  'prof.remember': 'Se souvenir de moi', 'prof.forgot': 'Mot de passe oublié ?', 'prof.sendCode': 'Envoyer le code',
  'prof.resetPassword': 'Réinitialiser le mot de passe', 'prof.emailCode': 'Code e-mail',
  'prof.emailCodeSent': 'Si ce compte existe, un code est en route. L’adresse ne change pas.',
  'prof.passwordReset': 'Mot de passe mis à jour. Connectez-vous avec le nouveau.',
  'prof.emailHint': 'Cette adresse ne peut pas changer. Un code confirme que vous pouvez l’ouvrir.',
  'prof.emailReady': 'Un code a été envoyé à votre e-mail. L’adresse ne change pas.',
  'prof.confirmCode': 'Vérifier le code',
  'prof.passwordHint': '12 caractères au minimum. Le changer ne désactive pas les autres contrôles.',
  'prof.phoneHint': 'Un code SMS confirme le numéro avant de l’enregistrer.',
  'prof.forgotHint': 'Mot de passe actuel oublié ? Utilisez Mot de passe oublié à la connexion. Un code part vers cette adresse.',
  'prof.authImage': 'Deux voyageurs à côté d’une voiture de location sur une route côtière',
  'prof.authBriefing': 'Briefing',
  'prof.authEyebrow': 'Locataire',
  'prof.authTitle': 'Vos réservations',
  'prof.authLead': 'Connectez-vous avec l’e-mail utilisé pour cette agence. Un nouveau compte confirme un numéro marocain avant de s’ouvrir.',
  'prof.authPointBookings': 'Les voyages en cours et passés restent sur ce site.',
  'prof.authPointPhone': 'L’inscription demande un code téléphone avant d’ouvrir le compte.',
  'prof.authStaff': 'Propriétaire ou comptoir ? Connectez-vous au bureau.',
  'prof.authWorking': 'Connexion…',
  'prof.authCreating': 'Création du compte…',
});
add('ar', {
  'prof.gold': 'عضو ذهبي', 'prof.license': 'رخصة السياقة',
  'prof.points': 'نقاط الولاء', 'prof.verified': 'سائق موثق',
  'prof.reward': 'المكافآت', 'prof.freeDay': 'يوم كراء مجاني',
  'prof.freeDaySub': 'صالح لحجز نهاية الأسبوع القادم',
  'prof.tabActive': 'الحجوزات النشطة', 'prof.tabPast': 'الرحلات السابقة',
  'prof.tabFav': 'المفضلة', 'prof.tabSettings': 'التفضيلات',
  'prof.emptyActive': 'لا توجد حجوزات نشطة',
  'prof.emptyActiveD': 'مستعد لرحلتك القادمة؟ تصفح الأسطول مع إلغاء مجاني.',
  'prof.browse': 'تصفح الأسطول', 'prof.totalPaid': 'المجموع المدفوع',
  'prof.modify': 'تعديل', 'prof.cancel': 'إلغاء',
  'prof.emptyPast': 'لا توجد رحلات سابقة مسجلة.',
  'prof.invoice': 'تحميل الفاتورة', 'prof.receipt': 'تم تحميل الفاتورة لـ',
  'prof.emptyFav': 'لا توجد سيارات محفوظة',
  'prof.emptyFavD': 'انقر على القلب في أي سيارة لحفظها هنا.',
  'prof.viewDetails': 'التفاصيل', 'prof.rentNow': 'اكترِ الآن',
  'prof.settingsT': 'الملف الشخصي والإشعارات',
  'prof.name': 'الاسم الكامل', 'prof.email': 'البريد الرئيسي', 'prof.phone': 'الهاتف',
  'prof.dl': 'رقم الرخصة', 'prof.save': 'حفظ', 'prof.saved': 'تم تحديث الملف!',
  'prof.cancelT': 'إلغاء الحجز', 'prof.cancelD': 'ستستلم استرداداً كاملاً فورياً بدون غرامات.',
  'prof.keep': 'إبقاء الحجز', 'prof.confirmCancel': 'تأكيد الإلغاء',
  'prof.modT': 'تعديل تواريخ', 'prof.modD': 'اختر تواريخ الاستلام والإرجاع الجديدة.',
  'prof.newPickup': 'تاريخ الاستلام الجديد', 'prof.newReturn': 'تاريخ الإرجاع الجديد',
  'prof.cancelBtn': 'إلغاء', 'prof.saveDates': 'حفظ',
  'prof.retLabel': 'الإرجاع:', 'prof.freeCancelPol': 'سياسة الإلغاء المجاني',
  'prof.signIn': 'تسجيل الدخول', 'prof.register': 'إنشاء حساب', 'prof.signOut': 'تسجيل الخروج',
  'prof.password': 'كلمة المرور (12 حرفًا على الأقل)', 'prof.accountHint': 'سجّل الدخول لعرض الحجوزات المرتبطة ببريدك.',
  'prof.firstName': 'الاسم', 'prof.lastName': 'النسب', 'prof.code': 'رمز الرسالة',
  'prof.codeHint': 'أدخل الرمز المرسل إلى', 'prof.demoCode': 'رمز التجربة', 'prof.resend': 'إرسال رمز جديد',
  'prof.verify': 'تأكيد الهاتف', 'prof.locale': 'اللغة', 'prof.avatar': 'صورة الملف',
  'prof.showPassword': 'إظهار', 'prof.hidePassword': 'إخفاء', 'prof.invitePassword': 'كلمة المرور',
  'prof.acceptInvite': 'قبول الدعوة', 'prof.inviteExpired': 'لم تعد هذه الدعوة صالحة.',
  'prof.confirmEmail': 'تأكيد البريد',
  'prof.verifyEmail': 'تأكيد البريد', 'prof.emailVerified': 'البريد مؤكد', 'prof.emailUnverified': 'البريد غير مؤكد',
  'prof.emailSent': 'أُرسل رابط التأكيد. العنوان لا يتغير.',
  'prof.currentPassword': 'كلمة المرور الحالية', 'prof.newPassword': 'كلمة مرور جديدة (12 حرفًا)',
  'prof.confirmPassword': 'تأكيد كلمة المرور الجديدة', 'prof.passwordMismatch': 'كلمتا المرور الجديدتان غير متطابقتين.',
  'prof.changePassword': 'تغيير كلمة المرور', 'prof.passwordSaved': 'تم تحديث كلمة المرور.',
  'prof.remember': 'تذكرني', 'prof.forgot': 'نسيت كلمة المرور؟', 'prof.sendCode': 'إرسال الرمز',
  'prof.resetPassword': 'إعادة تعيين كلمة المرور', 'prof.emailCode': 'رمز البريد',
  'prof.emailCodeSent': 'إذا كان هذا الحساب موجودًا فسيصلك رمز. العنوان لا يتغير.',
  'prof.passwordReset': 'تم تحديث كلمة المرور. سجّل الدخول بالجديدة.',
  'prof.emailHint': 'لا يمكن تغيير هذا العنوان. الرمز يؤكد أنك تستطيع فتحه.',
  'prof.emailReady': 'أُرسل رمز إلى بريدك. العنوان لا يتغير.',
  'prof.confirmCode': 'تأكيد الرمز',
  'prof.passwordHint': '12 حرفًا على الأقل. تغييرها لا يوقف عمليات التحقق الأخرى.',
  'prof.phoneHint': 'رمز SMS يؤكد الرقم قبل حفظه.',
  'prof.forgotHint': 'نسيت كلمة المرور الحالية؟ استخدم نسيت كلمة المرور عند الدخول. يصل الرمز إلى هذا العنوان.',
  'prof.authImage': 'مسافران بجانب سيارة كراء على طريق ساحلي',
  'prof.authBriefing': 'إحاطة',
  'prof.authEyebrow': 'مستأجر',
  'prof.authTitle': 'حجوزاتك',
  'prof.authLead': 'سجّل الدخول بالبريد الذي استخدمته مع هذه الوكالة. الحساب الجديد يؤكد رقمًا مغربيًا قبل أن يُفتح.',
  'prof.authPointBookings': 'الرحلات الجارية والسابقة تبقى على هذا الموقع.',
  'prof.authPointPhone': 'التسجيل يطلب رمز هاتف قبل فتح الحساب.',
  'prof.authStaff': 'مالك الوكالة أو مكتب الاستقبال؟ الدخول من المكتب.',
  'prof.authWorking': 'جارٍ الدخول…',
  'prof.authCreating': 'جارٍ إنشاء الحساب…',
});
add('es', {
  'prof.gold': 'Miembro Oro', 'prof.license': 'Carnet de conducir',
  'prof.points': 'Puntos', 'prof.verified': 'Conductor verificado',
  'prof.reward': 'Recompensas', 'prof.freeDay': '1 día gratis ganado',
  'prof.freeDaySub': 'Válido en tu próxima reserva de fin de semana',
  'prof.tabActive': 'Reservas activas', 'prof.tabPast': 'Viajes pasados',
  'prof.tabFav': 'Favoritos', 'prof.tabSettings': 'Preferencias',
  'prof.emptyActive': 'Sin reservas activas',
  'prof.emptyActiveD': '¿Listo para tu próximo viaje? Explora la flota con cancelación gratuita.',
  'prof.browse': 'Ver flota', 'prof.totalPaid': 'Total pagado',
  'prof.modify': 'Modificar', 'prof.cancel': 'Cancelar',
  'prof.emptyPast': 'Sin viajes anteriores.',
  'prof.invoice': 'Descargar factura', 'prof.receipt': 'Factura descargada para',
  'prof.emptyFav': 'Sin favoritos',
  'prof.emptyFavD': 'Toca el corazón de un vehículo para guardarlo aquí.',
  'prof.viewDetails': 'Detalles', 'prof.rentNow': 'Alquilar',
  'prof.settingsT': 'Perfil y notificaciones',
  'prof.name': 'Nombre legal', 'prof.email': 'Correo principal', 'prof.phone': 'Teléfono (SMS)',
  'prof.dl': 'N° de carnet', 'prof.save': 'Guardar', 'prof.saved': '¡Perfil actualizado!',
  'prof.cancelT': 'Cancelar reserva', 'prof.cancelD': 'recibirás un reembolso total inmediato sin penalizaciones.',
  'prof.keep': 'Mantener', 'prof.confirmCancel': 'Confirmar cancelación',
  'prof.modT': 'Modificar fechas de', 'prof.modD': 'Elige nuevas fechas de recogida y devolución.',
  'prof.newPickup': 'Nueva recogida', 'prof.newReturn': 'Nueva devolución',
  'prof.cancelBtn': 'Cancelar', 'prof.saveDates': 'Guardar',
  'prof.retLabel': 'Devolución:', 'prof.freeCancelPol': 'Política de cancelación gratuita',
  'prof.signIn': 'Entrar', 'prof.register': 'Crear cuenta', 'prof.signOut': 'Salir',
  'prof.password': 'Contraseña (mín. 12 caracteres)', 'prof.accountHint': 'Entra para ver las reservas de tu correo.',
  'prof.firstName': 'Nombre', 'prof.lastName': 'Apellidos', 'prof.code': 'Código SMS',
  'prof.codeHint': 'Introduce el código enviado a', 'prof.demoCode': 'Código de prueba', 'prof.resend': 'Enviar otro código',
  'prof.verify': 'Confirmar teléfono', 'prof.locale': 'Idioma', 'prof.avatar': 'Foto de perfil',
  'prof.showPassword': 'Mostrar', 'prof.hidePassword': 'Ocultar', 'prof.invitePassword': 'Contraseña',
  'prof.acceptInvite': 'Aceptar invitación', 'prof.inviteExpired': 'Esta invitación ya no es válida.',
  'prof.confirmEmail': 'Confirmar correo',
  'prof.verifyEmail': 'Verificar correo', 'prof.emailVerified': 'Correo verificado', 'prof.emailUnverified': 'Correo sin verificar',
  'prof.emailSent': 'Enlace de verificación enviado. La dirección no cambia.',
  'prof.currentPassword': 'Contraseña actual', 'prof.newPassword': 'Nueva contraseña (12 caracteres)',
  'prof.confirmPassword': 'Confirmar la nueva contraseña', 'prof.passwordMismatch': 'Las contraseñas nuevas no coinciden.',
  'prof.changePassword': 'Cambiar contraseña', 'prof.passwordSaved': 'Contraseña actualizada.',
  'prof.remember': 'Recordarme', 'prof.forgot': '¿Olvidaste la contraseña?', 'prof.sendCode': 'Enviar código',
  'prof.resetPassword': 'Restablecer contraseña', 'prof.emailCode': 'Código de correo',
  'prof.emailCodeSent': 'Si esta cuenta existe, va un código en camino. La dirección no cambia.',
  'prof.passwordReset': 'Contraseña actualizada. Entra con la nueva.',
  'prof.emailHint': 'Esta dirección no se puede cambiar. Un código confirma que puedes abrirla.',
  'prof.emailReady': 'Enviamos un código a tu correo. La dirección no cambia.',
  'prof.confirmCode': 'Verificar código',
  'prof.passwordHint': 'Al menos 12 caracteres. Cambiarla no desactiva las demás comprobaciones.',
  'prof.phoneHint': 'Un código SMS confirma el número antes de guardarlo.',
  'prof.forgotHint': '¿Olvidaste la contraseña actual? Usa ¿Olvidaste la contraseña? al entrar. El código llega a esta dirección.',
  'prof.authImage': 'Dos viajeros junto a un coche de alquiler en una carretera de costa',
  'prof.authBriefing': 'Briefing',
  'prof.authEyebrow': 'Arrendatario',
  'prof.authTitle': 'Tus reservas',
  'prof.authLead': 'Entra con el correo que usaste en esta agencia. Una cuenta nueva confirma un teléfono marroquí antes de abrirse.',
  'prof.authPointBookings': 'Los viajes activos y pasados se quedan en este sitio.',
  'prof.authPointPhone': 'El registro pide un código de teléfono antes de abrir la cuenta.',
  'prof.authStaff': '¿Dueño o mostrador? Entra en el escritorio.',
  'prof.authWorking': 'Entrando…',
  'prof.authCreating': 'Creando la cuenta…',
});


// ---------------- booking modal ----------------
add('en', {
  'book.s1': 'Step 1: Reservation Window & Hub', 'book.s2': 'Step 2: Protection Plan & Extras',
  'book.s3': 'Step 3: Primary Driver Credentials', 'book.s4': 'Step 4: Summary & Payment',
  'book.s5': 'Step 5: Sign your contract', 'book.daysTotal': 'Days Total',
  'book.unlimited': 'Unlimited Miles Included', 'book.pickLoc': 'Pick-up Location',
  'book.retLoc': 'Return / Drop-off Location', 'book.pickDT': 'Pick-up Date & Time',
  'book.retDT': 'Return Date & Time', 'book.checking': 'Checking availability for these dates…',
  'book.available': 'is available for', 'book.taken': 'Not available for these dates — please pick different dates.',
  'book.freeCancel': 'Free 100% Cancellation Guaranteed:',
  'book.freeCancelD': 'You can cancel this reservation anytime with zero fee up to 1 hour before scheduled pick-up.',
  'book.protection': 'Choose Protection Package',
  'book.protectionD': 'All rentals include state minimum third-party coverage. Upgrade for maximum peace of mind.',
  'book.basic': 'Basic Cover', 'book.basic1': 'Statutory third party liability',
  'book.basic3': 'Excludes tire and glass cover', 'book.popular': 'Popular',
  'book.standard': 'Standard Shield', 'book.cdw': 'CDW Collision Damage Waiver',
  'book.deduct': 'deductible', 'book.reduced': 'Reduced', 'book.theft': 'Theft protection included',
  'book.premium': 'Premium Total Shield', 'book.zeroDed': 'Zero Deductible on damage',
  'book.glass': 'Full Glass, Tire & Undercarriage', 'book.roadside': '24/7 Roadside VIP Assistance',
  'book.addons': 'Optional Equipment & Add-ons', 'book.driver': 'Primary Driver Full Name',
  'book.email': 'Email Address (for Confirmation & Voucher)', 'book.phone': 'Phone Number',
  'book.license': "Driver's License Number", 'book.age': 'I confirm that the primary driver is 21 years of age or older with a valid license.',
  'book.payMethod': 'Payment Method', 'book.card': 'Credit / Debit Card', 'book.counter': 'Pay at Pick-up Desk',
  'book.cardNum': 'Card Number', 'book.exp': 'Expiration (MM/YY)', 'book.cvc': 'Security CVC',
  'book.promo': 'Have a Promotional Code?', 'book.apply': 'Apply', 'book.applied': 'applied: 20% discount on total!',
  'book.appliedCode': 'Code', 'book.summary': 'Reservation Summary', 'book.protLabel': 'Protection',
  'book.addonsLbl': 'Add-ons', 'book.selected': 'selected', 'book.promoDisc': 'Promo Discount (20%)',
  'book.totalDue': 'Total Due', 'book.taxes': 'All taxes & fees included', 'book.forInfo': 'for information',
  'book.signTitle': 'One last step: sign your contract',
  'book.signD': 'Your vehicle is held for you. Redirecting to the secure e-signature page — your booking is confirmed once you sign.',
  'book.signNow': 'Sign the contract now', 'book.done': 'Done', 'book.viewProfile': 'View in My Profile',
  'book.back': 'Back', 'book.continue': 'Continue', 'book.confirm': 'CONFIRM & SIGN CONTRACT', 'book.booking': 'BOOKING…',
  'book.invalidPromo': 'Invalid code. Try "FLEETAVA20" for 20% off!',
  'book.justTaken': 'This vehicle was just booked for those dates — please pick different dates.',
  'book.takenErr': 'This vehicle is already booked for those dates — please pick different dates.',
  'book.failed': 'Booking failed — please try again.', 'book.network': 'Network error — please try again.',
  'book.notAvailTitle': 'Vehicle not available for these dates',
  'book.checkingTitle': 'Checking availability…', 'book.confirmTitle': 'Book and continue to e-signature',
  'book.days': 'days', 'book.depositLbl': 'security deposit', 'book.perDay': '/day',
});
add('fr', {
  'book.s1': 'Étape 1 : Dates & Agence', 'book.s2': 'Étape 2 : Protection & Options',
  'book.s3': 'Étape 3 : Conducteur principal', 'book.s4': 'Étape 4 : Récapitulatif & Paiement',
  'book.s5': 'Étape 5 : Signez votre contrat', 'book.daysTotal': 'Jours au total',
  'book.unlimited': 'Kilométrage illimité inclus', 'book.pickLoc': 'Lieu de retrait',
  'book.retLoc': 'Lieu de retour', 'book.pickDT': 'Date & heure de retrait',
  'book.retDT': 'Date & heure de retour', 'book.checking': 'Vérification des disponibilités…',
  'book.available': 'est disponible du', 'book.taken': 'Indisponible sur ces dates — choisissez d’autres dates.',
  'book.freeCancel': 'Annulation 100% gratuite garantie :',
  'book.freeCancelD': "Vous pouvez annuler sans frais jusqu'à 1 h avant le retrait.",
  'book.protection': 'Choisissez votre protection',
  'book.protectionD': 'Toutes les locations incluent la responsabilité civile minimale. Renforcez votre tranquillité.',
  'book.basic': 'Essentielle', 'book.basic1': 'Responsabilité civile légale',
  'book.basic3': 'Hors pneus et vitres', 'book.popular': 'Populaire',
  'book.standard': 'Standard', 'book.cdw': 'Rachat de franchise collision',
  'book.deduct': 'franchise', 'book.reduced': 'Franchise réduite', 'book.theft': 'Vol inclus',
  'book.premium': 'Premium', 'book.zeroDed': 'Zéro franchise en cas de dommage',
  'book.glass': 'Vitres, pneus & soubassement', 'book.roadside': 'Assistance VIP 24/7',
  'book.addons': 'Options & équipements', 'book.driver': 'Nom complet du conducteur',
  'book.email': 'E-mail (confirmation & bon)', 'book.phone': 'Téléphone',
  'book.license': 'N° de permis de conduire', 'book.age': 'Je confirme que le conducteur a 21 ans ou plus avec un permis valide.',
  'book.payMethod': 'Mode de paiement', 'book.card': 'Carte bancaire', 'book.counter': 'Paiement au comptoir',
  'book.cardNum': 'Numéro de carte', 'book.exp': 'Expiration (MM/AA)', 'book.cvc': 'CVC',
  'book.promo': 'Un code promo ?', 'book.apply': 'Appliquer', 'book.applied': 'appliqué : -20% sur le total !',
  'book.appliedCode': 'Code', 'book.summary': 'Récapitulatif', 'book.protLabel': 'Protection',
  'book.addonsLbl': 'Options', 'book.selected': 'sélectionnée(s)', 'book.promoDisc': 'Remise promo (20%)',
  'book.totalDue': 'Total dû', 'book.taxes': 'Taxes et frais inclus', 'book.forInfo': "pour info",
  'book.signTitle': 'Dernière étape : signez votre contrat',
  'book.signD': 'Votre véhicule est réservé. Redirection vers la signature électronique — réservation confirmée après signature.',
  'book.signNow': 'Signer le contrat', 'book.done': 'Terminé', 'book.viewProfile': 'Voir mon profil',
  'book.back': 'Retour', 'book.continue': 'Continuer', 'book.confirm': 'CONFIRMER & SIGNER', 'book.booking': 'RÉSERVATION…',
  'book.invalidPromo': 'Code invalide. Essayez « FLEETAVA20 » pour -20% !',
  'book.justTaken': 'Ce véhicule vient d’être réservé sur ces dates — choisissez d’autres dates.',
  'book.takenErr': 'Ce véhicule est déjà réservé sur ces dates — choisissez d’autres dates.',
  'book.failed': 'Échec de la réservation — réessayez.', 'book.network': 'Erreur réseau — réessayez.',
  'book.notAvailTitle': 'Véhicule indisponible sur ces dates',
  'book.checkingTitle': 'Vérification…', 'book.confirmTitle': 'Réserver et passer à la signature',
  'book.days': 'jours', 'book.depositLbl': 'dépôt de garantie', 'book.perDay': '/jour',
});
add('ar', {
  'book.s1': 'الخطوة 1: التواريخ والوكالة', 'book.s2': 'الخطوة 2: الحماية والإضافات',
  'book.s3': 'الخطوة 3: بيانات السائق', 'book.s4': 'الخطوة 4: الملخص والدفع',
  'book.s5': 'الخطوة 5: وقّع عقدك', 'book.daysTotal': 'مجموع الأيام',
  'book.unlimited': 'كيلومترات غير محدودة', 'book.pickLoc': 'مكان الاستلام',
  'book.retLoc': 'مكان الإرجاع', 'book.pickDT': 'تاريخ ووقت الاستلام',
  'book.retDT': 'تاريخ ووقت الإرجاع', 'book.checking': 'جارٍ التحقق من التوفر…',
  'book.available': 'متاح من', 'book.taken': 'غير متاح في هذه التواريخ — اختر تواريخ أخرى.',
  'book.freeCancel': 'إلغاء مجاني 100% مضمون:',
  'book.freeCancelD': 'يمكنك الإلغاء بدون رسوم حتى ساعة قبل الاستلام.',
  'book.protection': 'اختر باقة الحماية',
  'book.protectionD': 'جميع عمليات الكراء تشمل المسؤولية المدنية الدنيا.',
  'book.basic': 'أساسية', 'book.basic1': 'المسؤولية المدنية القانونية',
  'book.basic3': 'بدون الإطارات والزجاج', 'book.popular': 'الأكثر طلباً',
  'book.standard': 'قياسية', 'book.cdw': 'التنازل عن أضرار التصادم',
  'book.deduct': 'الفرنشيز', 'book.reduced': 'فرنشيز مخفض', 'book.theft': 'الحماية من السرقة مشمولة',
  'book.premium': 'ممتازة', 'book.zeroDed': 'صفر فرنشيز عند الضرر',
  'book.glass': 'الزجاج والإطارات', 'book.roadside': 'مساعدة VIP على الطريق 24/7',
  'book.addons': 'إضافات اختيارية', 'book.driver': 'الاسم الكامل للسائق',
  'book.email': 'البريد (التأكيد والقسيمة)', 'book.phone': 'الهاتف',
  'book.license': 'رقم رخصة السياقة', 'book.age': 'أؤكد أن السائق يبلغ 21 سنة فأكثر برخصة سارية.',
  'book.payMethod': 'طريقة الدفع', 'book.card': 'بطاقة بنكية', 'book.counter': 'الدفع في الوكالة',
  'book.cardNum': 'رقم البطاقة', 'book.exp': 'الانتهاء', 'book.cvc': 'CVC',
  'book.promo': 'لديك رمز ترويجي؟', 'book.apply': 'تطبيق', 'book.applied': 'مطبق: خصم 20%!',
  'book.appliedCode': 'الرمز', 'book.summary': 'الملخص', 'book.protLabel': 'الحماية',
  'book.addonsLbl': 'الإضافات', 'book.selected': 'محددة', 'book.promoDisc': 'خصم (20%)',
  'book.totalDue': 'المجموع', 'book.taxes': 'شامل الضرائب والرسوم', 'book.forInfo': 'للعلم',
  'book.signTitle': 'خطوة أخيرة: وقّع عقدك',
  'book.signD': 'سيارتك محجوزة. جارٍ التحويل إلى التوقيع الإلكتروني — يُؤكَّد الحجز بعد التوقيع.',
  'book.signNow': 'وقّع العقد الآن', 'book.done': 'تم', 'book.viewProfile': 'عرض حسابي',
  'book.back': 'رجوع', 'book.continue': 'متابعة', 'book.confirm': 'تأكيد وتوقيع', 'book.booking': 'جارٍ الحجز…',
  'book.invalidPromo': 'رمز غير صالح. جرّب FLEETAVA20 لخصم 20%!',
  'book.justTaken': 'تم حجز هذه السيارة للتو في هذه التواريخ — اختر تواريخ أخرى.',
  'book.takenErr': 'هذه السيارة محجوزة في هذه التواريخ — اختر تواريخ أخرى.',
  'book.failed': 'فشل الحجز — حاول مجدداً.', 'book.network': 'خطأ في الشبكة — حاول مجدداً.',
  'book.notAvailTitle': 'السيارة غير متاحة في هذه التواريخ',
  'book.checkingTitle': 'جارٍ التحقق…', 'book.confirmTitle': 'احجز وتابع إلى التوقيع',
  'book.days': 'أيام', 'book.depositLbl': 'وديعة الضمان', 'book.perDay': '/يوم',
});
add('es', {
  'book.s1': 'Paso 1: Fechas y agencia', 'book.s2': 'Paso 2: Protección y extras',
  'book.s3': 'Paso 3: Datos del conductor', 'book.s4': 'Paso 4: Resumen y pago',
  'book.s5': 'Paso 5: Firma tu contrato', 'book.daysTotal': 'Días totales',
  'book.unlimited': 'Kilometraje ilimitado incluido', 'book.pickLoc': 'Lugar de recogida',
  'book.retLoc': 'Lugar de devolución', 'book.pickDT': 'Fecha y hora de recogida',
  'book.retDT': 'Fecha y hora de devolución', 'book.checking': 'Comprobando disponibilidad…',
  'book.available': 'disponible del', 'book.taken': 'No disponible en esas fechas — elige otras.',
  'book.freeCancel': 'Cancelación 100% gratuita garantizada:',
  'book.freeCancelD': 'Puedes cancelar sin coste hasta 1 h antes de la recogida.',
  'book.protection': 'Elige tu protección',
  'book.protectionD': 'Todos los alquileres incluyen la cobertura mínima. Mejora tu tranquilidad.',
  'book.basic': 'Básica', 'book.basic1': 'Responsabilidad civil legal',
  'book.basic3': 'Sin neumáticos ni lunas', 'book.popular': 'Popular',
  'book.standard': 'Estándar', 'book.cdw': 'Exención de daños por colisión',
  'book.deduct': 'franquicia', 'book.reduced': 'Franquicia reducida', 'book.theft': 'Robo incluido',
  'book.premium': 'Premium', 'book.zeroDed': 'Cero franquicia en daños',
  'book.glass': 'Lunas, neumáticos y bajos', 'book.roadside': 'Asistencia VIP 24/7',
  'book.addons': 'Extras opcionales', 'book.driver': 'Nombre completo del conductor',
  'book.email': 'Correo (confirmación y bono)', 'book.phone': 'Teléfono',
  'book.license': 'N° de carnet', 'book.age': 'Confirmo que el conductor tiene 21+ años con carnet válido.',
  'book.payMethod': 'Método de pago', 'book.card': 'Tarjeta', 'book.counter': 'Pago en oficina',
  'book.cardNum': 'Número de tarjeta', 'book.exp': 'Vencimiento (MM/AA)', 'book.cvc': 'CVC',
  'book.promo': '¿Tienes un código?', 'book.apply': 'Aplicar', 'book.applied': 'aplicado: ¡20% de descuento!',
  'book.appliedCode': 'Código', 'book.summary': 'Resumen', 'book.protLabel': 'Protección',
  'book.addonsLbl': 'Extras', 'book.selected': 'elegidos', 'book.promoDisc': 'Descuento (20%)',
  'book.totalDue': 'Total', 'book.taxes': 'Impuestos incluidos', 'book.forInfo': 'informativo',
  'book.signTitle': 'Último paso: firma tu contrato',
  'book.signD': 'Tu vehículo está reservado. Redirigiendo a la firma electrónica — la reserva se confirma al firmar.',
  'book.signNow': 'Firmar ahora', 'book.done': 'Listo', 'book.viewProfile': 'Ver mi perfil',
  'book.back': 'Atrás', 'book.continue': 'Continuar', 'book.confirm': 'CONFIRMAR Y FIRMAR', 'book.booking': 'RESERVANDO…',
  'book.invalidPromo': 'Código inválido. ¡Prueba FLEETAVA20 para -20%!',
  'book.justTaken': 'Este coche se acaba de reservar en esas fechas — elige otras.',
  'book.takenErr': 'Este coche ya está reservado en esas fechas — elige otras.',
  'book.failed': 'Reserva fallida — inténtalo de nuevo.', 'book.network': 'Error de red — inténtalo de nuevo.',
  'book.notAvailTitle': 'Coche no disponible en esas fechas',
  'book.checkingTitle': 'Comprobando…', 'book.confirmTitle': 'Reservar y firmar',
  'book.days': 'días', 'book.depositLbl': 'depósito de garantía', 'book.perDay': '/día',
});


// ---------------- locations / deals / services ----------------
add('en', {
  'loc.kicker': 'NATIONWIDE PICK-UP HUBS', 'loc.title': 'fleetava Rental Locations',
  'loc.sub': 'Pick up your vehicle conveniently at all major international airports or downtown branches with express curbside return and 24/7 key drop.',
  'loc.searchPh': 'Search by city...', 'loc.airportOnly': 'Airport Locations Only',
  'loc.ready': 'Vehicles in Hub Ready Today', 'loc.rent': 'Rent From This Location',
  'deal.kicker': 'EXCLUSIVE OFFERS & SAVINGS', 'deal.title': 'Special Deals & Promotions',
  'deal.sub': 'Save on your next road trip, family vacation, or corporate trip with our verified promo codes and discount vouchers.',
  'deal.apply': 'APPLY & BOOK WITH THIS DEAL', 'deal.valid': 'Valid until:', 'deal.oneClick': '1-Click Apply',
  'deal.d1t': 'First Journey 20% Discount', 'deal.d1tag': 'WELCOME DEAL',
  'deal.d1d': 'Get 20% discount on your first booking with fleetava. Valid across all economy, SUV, and luxury classes.',
  'deal.d1v': 'October 31, 2026', 'deal.d1terms': 'Minimum 2-day rental period.',
  'deal.d2t': 'Weekend Escape Special', 'deal.d2tag': 'WEEKEND RATE',
  'deal.d2d': 'Pick up Friday after 12:00 PM and return Monday before 10:00 AM for reduced weekend rate and unlimited mileage.',
  'deal.d2v': 'Ongoing 2026', 'deal.d2terms': 'Valid on Convertible and SUV vehicles.',
  'deal.d3t': 'Monthly & Business Long-Term Pass', 'deal.d3tag': 'LONG TERM',
  'deal.d3d': 'Special rate for corporate accounts and digital nomads renting for 14 days or longer. Full insurance included.',
  'deal.d3v': 'Ongoing 2026', 'deal.d3terms': 'Requires minimum 14 consecutive rental days.',
  'svc.kicker': 'WHAT WE OFFER', 'svc.title': 'fleetava Premium Mobility Services',
  'svc.sub': 'From flexible daily rentals to corporate fleet provisioning and long-term passes, explore how we power your journeys.',
  'svc.cta': 'Inquire / Book Service',
  'svc.1t': 'Daily & Weekend Car Rental',
  'svc.1d': 'Transparent daily rates with zero hidden charges, unlimited mileage, and instant online check-in so you can drive away in under 3 minutes.',
  'svc.2t': 'Corporate Fleet Management',
  'svc.2d': 'Dedicated account managers, consolidated monthly billing, customized company insurance, and flexible swap privileges for business teams.',
  'svc.3t': 'Long-Term & Monthly Subscription',
  'svc.3d': 'Enjoy all the freedom of having a car without long leases, depreciation, or maintenance hassles. Swap vehicles as your lifestyle changes.',
  'svc.4t': 'Airport VIP Curbside Delivery',
  'svc.4d': 'Land and step directly into your vehicle at the terminal curb. No rental shuttles or counter lines required.',
  'svc.5t': 'Comprehensive Shield Coverage',
  'svc.5d': 'Zero-deductible CDW protection, glass and tire coverage, and 24/7 roadside assistance across Morocco.',
  'svc.6t': '24/7 Concierge & Roadside Support',
  'svc.6d': 'Our multilingual support dispatchers are always available to help with itinerary questions, route recommendations, or roadside needs.',
});
add('fr', {
  'loc.kicker': 'AGENCES PARTOUT AU MAROC', 'loc.title': 'Nos agences fleetava',
  'loc.sub': 'Récupérez votre véhicule dans nos agences aéroport et centre-ville avec restitution express et boîte à clés 24/7.',
  'loc.searchPh': 'Rechercher une ville...', 'loc.airportOnly': 'Aéroports uniquement',
  'loc.ready': 'Véhicules prêts aujourd’hui', 'loc.rent': 'Louer depuis cette agence',
  'deal.kicker': 'OFFRES EXCLUSIVES', 'deal.title': 'Offres & Promotions',
  'deal.sub': 'Économisez sur votre prochain voyage avec nos codes promo vérifiés.',
  'deal.apply': 'APPLIQUER & RÉSERVER', 'deal.valid': 'Valable jusqu’au :', 'deal.oneClick': 'Application en 1 clic',
  'deal.d1t': 'Première location -20%', 'deal.d1tag': 'BIENVENUE',
  'deal.d1d': "-20% sur votre première réservation fleetava. Valable sur toutes les catégories.",
  'deal.d1v': '31 octobre 2026', 'deal.d1terms': 'Location de 2 jours minimum.',
  'deal.d2t': 'Escapade week-end', 'deal.d2tag': 'TARIF WEEK-END',
  'deal.d2d': 'Retrait vendredi après 12h, retour lundi avant 10h avec kilométrage illimité.',
  'deal.d2v': 'Toute l’année 2026', 'deal.d2terms': 'Valable sur SUV et cabriolets.',
  'deal.d3t': 'Pass longue durée pro', 'deal.d3tag': 'LONGUE DURÉE',
  'deal.d3d': 'Tarif spécial entreprises et nomades digitaux dès 14 jours. Assurance complète incluse.',
  'deal.d3v': 'Toute l’année 2026', 'deal.d3terms': '14 jours consécutifs minimum.',
  'svc.kicker': 'NOS SERVICES', 'svc.title': 'Services mobilité fleetava',
  'svc.sub': 'De la location journalière aux flottes entreprises, découvrez comment nous propulsons vos trajets.',
  'svc.cta': 'Demander / Réserver',
  'svc.1t': 'Location journalière & week-end',
  'svc.1d': 'Tarifs transparents, kilométrage illimité et enregistrement en ligne instantané.',
  'svc.2t': 'Flottes entreprises',
  'svc.2d': 'Interlocuteur dédié, facturation mensuelle consolidée et assurance sur mesure.',
  'svc.3t': 'Abonnement longue durée',
  'svc.3d': "Tous les avantages d'une voiture sans crédit-bail ni entretien. Changez de véhicule à volonté.",
  'svc.4t': 'Livraison VIP aéroport',
  'svc.4d': 'Atterrissez et montez directement dans votre véhicule au terminal. Ni navette ni comptoir.',
  'svc.5t': 'Protection complète',
  'svc.5d': 'CDW sans franchise, vitres et pneus couverts, assistance 24/7 dans tout le Maroc.',
  'svc.6t': 'Conciergerie 24/7',
  'svc.6d': 'Nos équipes multilingues vous aident pour itinéraires, conseils et dépannage.',
});
add('ar', {
  'loc.kicker': 'وكالات في كل المغرب', 'loc.title': 'وكالات fleetava',
  'loc.sub': 'استلم سيارتك في المطار أو وسط المدينة مع إرجاع سريع وصندوق مفاتيح 24/7.',
  'loc.searchPh': 'ابحث بمدينة...', 'loc.airportOnly': 'المطارات فقط',
  'loc.ready': 'سيارة جاهزة اليوم', 'loc.rent': 'اكترِ من هذه الوكالة',
  'deal.kicker': 'عروض حصرية', 'deal.title': 'العروض والتخفيضات',
  'deal.sub': 'وفّر في رحلتك القادمة مع رموزنا الترويجية الموثوقة.',
  'deal.apply': 'طبّق واحجز', 'deal.valid': 'صالح حتى:', 'deal.oneClick': 'تطبيق بنقرة',
  'deal.d1t': 'خصم 20% على أول رحلة', 'deal.d1tag': 'عرض الترحيب',
  'deal.d1d': 'خصم 20% على أول حجز مع fleetava لجميع الفئات.',
  'deal.d1v': '31 أكتوبر 2026', 'deal.d1terms': 'مدة دنيا يومان.',
  'deal.d2t': 'عرض نهاية الأسبوع', 'deal.d2tag': 'تعرفة نهاية الأسبوع',
  'deal.d2d': 'استلام الجمعة بعد 12:00 وإرجاع الاثنين قبل 10:00 مع كيلومترات غير محدودة.',
  'deal.d2v': 'طوال 2026', 'deal.d2terms': 'صالح لسيارات SUV والمكشوفة.',
  'deal.d3t': 'بطاقة الأعمال طويلة المدة', 'deal.d3tag': 'مدة طويلة',
  'deal.d3d': 'سعر خاص للشركات من 14 يوماً فأكثر مع تأمين شامل.',
  'deal.d3v': 'طوال 2026', 'deal.d3terms': '14 يوماً متتالية على الأقل.',
  'svc.kicker': 'خدماتنا', 'svc.title': 'خدمات fleetava للتنقل',
  'svc.sub': 'من الكراء اليومي إلى أساطيل الشركات، اكتشف كيف نسهّل تنقلاتك.',
  'svc.cta': 'اطلب / احجز',
  'svc.1t': 'كراء يومي ونهاية الأسبوع',
  'svc.1d': 'أسعار شفافة وكيلومترات غير محدودة وتسجيل online فوري.',
  'svc.2t': 'تدبير أساطيل الشركات',
  'svc.2d': 'مسؤول مخصص وفوترة شهرية موحدة وتأمين مخصص للشركات.',
  'svc.3t': 'اشتراك شهري',
  'svc.3d': 'كل مزايا السيارة دون قرض أو صيانة. بدّل السيارة متى شئت.',
  'svc.4t': 'توصيل VIP في المطار',
  'svc.4d': 'اهبط واركب سيارتك مباشرة عند المحطة. لا حافلات ولا طوابير.',
  'svc.5t': 'تغطية شاملة',
  'svc.5d': 'حماية بدون فرنشيز وزجاج وإطارات ومساعدة 24/7 في كل المغرب.',
  'svc.6t': 'دعم واستقبال 24/7',
  'svc.6d': 'فرقنا متعددة اللغات رهن إشارتك للمسارات والنصائح والمساعدة.',
});
add('es', {
  'loc.kicker': 'AGENCIES EN TODO MARRUECOS', 'loc.title': 'Nuestras agencias fleetava',
  'loc.sub': 'Recoge tu vehículo en aeropuerto o centro, con devolución exprés y buzón 24/7.',
  'loc.searchPh': 'Buscar ciudad...', 'loc.airportOnly': 'Solo aeropuertos',
  'loc.ready': 'Vehículos listos hoy', 'loc.rent': 'Alquilar aquí',
  'deal.kicker': 'OFERTAS EXCLUSIVAS', 'deal.title': 'Ofertas y promociones',
  'deal.sub': 'Ahorra en tu próximo viaje con nuestros códigos verificados.',
  'deal.apply': 'APLICAR Y RESERVAR', 'deal.valid': 'Válido hasta:', 'deal.oneClick': 'Aplicar en 1 clic',
  'deal.d1t': 'Primer viaje -20%', 'deal.d1tag': 'BIENVENIDA',
  'deal.d1d': '-20% en tu primera reserva fleetava. Válido en todas las categorías.',
  'deal.d1v': '31 de octubre de 2026', 'deal.d1terms': 'Mínimo 2 días.',
  'deal.d2t': 'Escapada de fin de semana', 'deal.d2tag': 'FIN DE SEMANA',
  'deal.d2d': 'Recoge el viernes tras las 12:00 y devuelve el lunes antes de las 10:00.',
  'deal.d2v': 'Todo 2026', 'deal.d2terms': 'Válido en SUV y descapotables.',
  'deal.d3t': 'Pase mensual empresas', 'deal.d3tag': 'LARGA DURACIÓN',
  'deal.d3d': 'Tarifa especial para empresas desde 14 días. Seguro completo incluido.',
  'deal.d3v': 'Todo 2026', 'deal.d3terms': 'Mínimo 14 días consecutivos.',
  'svc.kicker': 'NUESTROS SERVICIOS', 'svc.title': 'Servicios de movilidad fleetava',
  'svc.sub': 'Del alquiler diario a flotas corporativas, descubre cómo impulsamos tus viajes.',
  'svc.cta': 'Consultar / Reservar',
  'svc.1t': 'Alquiler diario y fin de semana',
  'svc.1d': 'Tarifas transparentes, kilometraje ilimitado y check-in online instantáneo.',
  'svc.2t': 'Flotas corporativas',
  'svc.2d': 'Gestor dedicado, facturación mensual y seguro a medida.',
  'svc.3t': 'Suscripción mensual',
  'svc.3d': 'Todas las ventajas del coche sin leasing ni mantenimiento.',
  'svc.4t': 'Entrega VIP en aeropuerto',
  'svc.4d': 'Aterriza y sube a tu coche en la terminal. Sin lanzaderas ni colas.',
  'svc.5t': 'Cobertura total',
  'svc.5d': 'CDW sin franquicia, lunas y neumáticos, asistencia 24/7 en Marruecos.',
  'svc.6t': 'Conserjería 24/7',
  'svc.6d': 'Equipos multilingües para rutas, consejos y asistencia.',
});

add('en', {
  'demo.banner': 'Demonstration site. Names, phones, and identity numbers are fictional. No payment is captured.',
});
add('fr', {
  'demo.banner': 'Site de démonstration. Les noms, téléphones et numéros d’identité sont fictifs. Aucun paiement n’est encaissé.',
});
add('ar', {
  'demo.banner': 'موقع للعرض. الأسماء والهواتف وأرقام الهوية وهمية. لا يتم تحصيل أي مبلغ.',
});
add('es', {
  'demo.banner': 'Sitio de demostración. Los nombres, teléfonos y documentos son ficticios. No se cobra ningún pago.',
});

const SIGN: Record<SiteLang, Dict> = {
  en: {
    'sign.title': 'fleetava — Sign the contract',
    'sign.secure': 'Personal secure link',
    'sign.loading': 'Loading the contract…',
    'sign.invalidTitle': 'Invalid link',
    'sign.invalidBody': 'This signing link is unknown. Contact the agency for a new one.',
    'sign.expiredTitle': 'Link expired',
    'sign.expiredBody': 'This link has expired. Contact the agency for a new one.',
    'sign.usedTitle': 'Link already used',
    'sign.usedBody': 'This link was already used. Contact the agency if that is a mistake.',
    'sign.lockedTitle': 'Session locked',
    'sign.lockedBody': 'Too many wrong codes. Ask the agency for a new link.',
    'sign.success': 'Contract signed',
    'sign.already': 'Contract already signed',
    'sign.confirmed': 'Your reservation is confirmed. Bring your identity document to the agency.',
    'sign.download': 'Download the PDF',
    'sign.stepContract': 'Contract',
    'sign.stepVerify': 'Verification',
    'sign.stepSign': 'Signature',
    'sign.stepDone': 'Done',
    'sign.client': 'Client',
    'sign.license': 'License',
    'sign.vehicle': 'Vehicle',
    'sign.pickup': 'Pickup',
    'sign.dropoff': 'Return',
    'sign.duration': 'Duration',
    'sign.days': 'day(s)',
    'sign.subtotal': 'Subtotal',
    'sign.protection': 'Protection',
    'sign.options': 'Options',
    'sign.total': 'Total',
    'sign.advance': 'Advance',
    'sign.deposit': 'Deposit',
    'sign.terms': 'Terms',
    'sign.accept': 'I have read and accept the contract terms.',
    'sign.continue': 'Continue',
    'sign.phoneTitle': 'Phone check',
    'sign.phoneSent': 'A code will be sent to',
    'sign.phoneLabel': 'Reservation phone number',
    'sign.send': 'Send the code',
    'sign.sending': 'Sending…',
    'sign.demo': 'Demonstration code',
    'sign.codeTitle': '6-digit code',
    'sign.verifying': 'Checking…',
    'sign.verify': 'Verify',
    'sign.backContract': 'Back to the contract',
    'sign.signTitle': 'Signature',
    'sign.nameLabel': 'Full name, as it should appear on the contract',
    'sign.namePlaceholder': 'Your full name',
    'sign.draw': 'Draw your signature below',
    'sign.consent': 'I accept that this drawn signature plus the SMS code is my signature, and that my data is used to manage the contract.',
    'sign.signing': 'Signing…',
    'sign.sign': 'Sign the contract',
    'sign.backVerify': 'Back to verification',
    'sign.needPhone': 'Enter the phone number on the reservation.',
    'sign.sendFail': 'The code could not be sent.',
    'sign.sent': 'Code sent. It expires in a few minutes.',
    'sign.retry': 'Try again.',
    'sign.needCode': 'The code must be 6 digits.',
    'sign.badCode': 'Incorrect code.',
    'sign.needName': 'Enter your full name.',
    'sign.needDraw': 'Draw your signature in the box.',
    'sign.needConsent': 'Tick the consent box to sign.',
    'sign.badSignature': 'The signature is invalid. Clear it and try again.',
    'sign.signFail': 'The contract could not be signed.',
    'sign.recorded': 'Recorded signatures',
    'site.catalogError': 'The fleet could not be loaded. Refresh the page, or contact the agency.',
  },
  fr: {
    'sign.title': 'fleetava — Signature du contrat',
    'sign.secure': 'Lien personnel et sécurisé',
    'sign.loading': 'Chargement du contrat…',
    'sign.invalidTitle': 'Lien invalide',
    'sign.invalidBody': 'Ce lien de signature est inconnu. Contactez l’agence pour recevoir un nouveau lien.',
    'sign.expiredTitle': 'Lien expiré',
    'sign.expiredBody': 'Ce lien a expiré. Contactez l’agence pour recevoir un nouveau lien.',
    'sign.lockedTitle': 'Session verrouillée',
    'sign.lockedBody': 'Trop de tentatives incorrectes. Demandez un nouveau lien à l’agence.',
    'sign.usedTitle': 'Lien déjà utilisé',
    'sign.usedBody': 'Ce lien a déjà servi. Contactez l’agence si vous pensez qu’il s’agit d’une erreur.',
    'sign.success': 'Contrat signé',
    'sign.already': 'Contrat déjà signé',
    'sign.confirmed': 'Votre réservation est confirmée. Présentez-vous à l’agence avec votre pièce d’identité.',
    'sign.download': 'Télécharger le PDF',
    'sign.stepContract': 'Contrat',
    'sign.stepVerify': 'Vérification',
    'sign.stepSign': 'Signature',
    'sign.stepDone': 'Terminé',
    'sign.client': 'Client',
    'sign.license': 'Permis',
    'sign.vehicle': 'Véhicule',
    'sign.pickup': 'Retrait',
    'sign.dropoff': 'Retour',
    'sign.duration': 'Durée',
    'sign.days': 'jour(s)',
    'sign.subtotal': 'Sous-total',
    'sign.protection': 'Protection',
    'sign.options': 'Options',
    'sign.total': 'Total',
    'sign.advance': 'Avance',
    'sign.deposit': 'Caution',
    'sign.terms': 'Conditions générales',
    'sign.accept': 'J’ai lu et j’accepte les conditions générales du contrat.',
    'sign.continue': 'Continuer',
    'sign.phoneTitle': 'Vérification du téléphone',
    'sign.phoneSent': 'Un code sera envoyé au numéro',
    'sign.phoneLabel': 'Numéro de téléphone de la réservation',
    'sign.send': 'Recevoir le code',
    'sign.sending': 'Envoi…',
    'sign.demo': 'Mode démo',
    'sign.codeTitle': 'Code à 6 chiffres',
    'sign.verifying': 'Vérification…',
    'sign.verify': 'Vérifier',
    'sign.backContract': 'Retour au contrat',
    'sign.signTitle': 'Signature',
    'sign.nameLabel': 'Nom complet, tel qu’il figurera au contrat',
    'sign.namePlaceholder': 'Votre nom complet',
    'sign.draw': 'Tracez votre signature ci-dessous',
    'sign.consent': 'Je reconnais que ma signature manuscrite ci-dessus, combinée au code reçu par SMS, vaut signature, et j’accepte le traitement de mes données pour la gestion du contrat.',
    'sign.signing': 'Signature…',
    'sign.sign': 'Signer le contrat',
    'sign.backVerify': 'Retour à la vérification',
    'sign.needPhone': 'Saisissez le numéro de téléphone de la réservation.',
    'sign.sendFail': 'Envoi du code impossible.',
    'sign.sent': 'Code envoyé par SMS. Il expire dans quelques minutes.',
    'sign.retry': 'Réessayez.',
    'sign.needCode': 'Le code doit contenir 6 chiffres.',
    'sign.badCode': 'Code incorrect.',
    'sign.needName': 'Saisissez votre nom complet.',
    'sign.needDraw': 'Tracez votre signature dans le cadre.',
    'sign.needConsent': 'Vous devez cocher le consentement pour signer.',
    'sign.badSignature': 'Signature invalide. Effacez et recommencez.',
    'sign.signFail': 'Signature impossible.',
    'sign.recorded': 'Signatures enregistrées',
    'site.catalogError': 'La flotte n’a pas pu être chargée. Actualisez la page, ou contactez l’agence.',
  },
  ar: {
    'sign.title': 'درايف إكس — توقيع العقد',
    'sign.secure': 'رابط شخصي وآمن',
    'sign.loading': 'جارٍ تحميل العقد…',
    'sign.invalidTitle': 'رابط غير صالح',
    'sign.invalidBody': 'رابط التوقيع هذا غير معروف. اتصل بالوكالة للحصول على رابط جديد.',
    'sign.expiredTitle': 'انتهت صلاحية الرابط',
    'sign.expiredBody': 'انتهت صلاحية هذا الرابط. اتصل بالوكالة للحصول على رابط جديد.',
    'sign.usedTitle': 'تم استخدام الرابط',
    'sign.usedBody': 'استُخدم هذا الرابط من قبل. اتصل بالوكالة إذا كان ذلك خطأ.',
    'sign.lockedTitle': 'الجلسة مقفلة',
    'sign.lockedBody': 'محاولات خاطئة كثيرة. اطلب رابطًا جديدًا من الوكالة.',
    'sign.success': 'تم توقيع العقد',
    'sign.already': 'العقد موقّع مسبقًا',
    'sign.confirmed': 'تم تأكيد حجزك. أحضر وثيقة الهوية إلى الوكالة.',
    'sign.download': 'تنزيل ملف PDF',
    'sign.stepContract': 'العقد',
    'sign.stepVerify': 'التحقق',
    'sign.stepSign': 'التوقيع',
    'sign.stepDone': 'تم',
    'sign.client': 'الزبون',
    'sign.license': 'الرخصة',
    'sign.vehicle': 'السيارة',
    'sign.pickup': 'الاستلام',
    'sign.dropoff': 'الإرجاع',
    'sign.duration': 'المدة',
    'sign.days': 'يوم',
    'sign.subtotal': 'المجموع الفرعي',
    'sign.protection': 'التأمين',
    'sign.options': 'الإضافات',
    'sign.total': 'المجموع',
    'sign.advance': 'التسبيق',
    'sign.deposit': 'الضمان',
    'sign.terms': 'الشروط',
    'sign.accept': 'قرأت شروط العقد وأوافق عليها.',
    'sign.continue': 'متابعة',
    'sign.phoneTitle': 'التحقق من الهاتف',
    'sign.phoneSent': 'سيُرسل رمز إلى الرقم',
    'sign.phoneLabel': 'هاتف الحجز',
    'sign.send': 'إرسال الرمز',
    'sign.sending': 'جارٍ الإرسال…',
    'sign.demo': 'رمز العرض',
    'sign.codeTitle': 'رمز من 6 أرقام',
    'sign.verifying': 'جارٍ التحقق…',
    'sign.verify': 'تحقق',
    'sign.backContract': 'العودة إلى العقد',
    'sign.signTitle': 'التوقيع',
    'sign.nameLabel': 'الاسم الكامل كما سيظهر في العقد',
    'sign.namePlaceholder': 'اسمك الكامل',
    'sign.draw': 'ارسم توقيعك في الإطار',
    'sign.consent': 'أوافق على أن هذا التوقيع مع رمز الرسالة هو توقيعي، وعلى استخدام بياناتي لإدارة العقد.',
    'sign.signing': 'جارٍ التوقيع…',
    'sign.sign': 'توقيع العقد',
    'sign.backVerify': 'العودة إلى التحقق',
    'sign.needPhone': 'أدخل رقم الهاتف المسجل في الحجز.',
    'sign.sendFail': 'تعذر إرسال الرمز.',
    'sign.sent': 'أُرسل الرمز. تنتهي صلاحيته بعد دقائق.',
    'sign.retry': 'حاول مرة أخرى.',
    'sign.needCode': 'يجب أن يتكون الرمز من 6 أرقام.',
    'sign.badCode': 'الرمز غير صحيح.',
    'sign.needName': 'أدخل اسمك الكامل.',
    'sign.needDraw': 'ارسم توقيعك في الإطار.',
    'sign.needConsent': 'يجب الموافقة قبل التوقيع.',
    'sign.badSignature': 'التوقيع غير صالح. امسحه وحاول مرة أخرى.',
    'sign.signFail': 'تعذر توقيع العقد.',
    'sign.recorded': 'التوقيعات المسجلة',
    'site.catalogError': 'تعذر تحميل الأسطول. حدّث الصفحة، أو اتصل بالوكالة.',
  },
  es: {
    'sign.title': 'fleetava — Firma del contrato',
    'sign.secure': 'Enlace personal y seguro',
    'sign.loading': 'Cargando el contrato…',
    'sign.invalidTitle': 'Enlace no válido',
    'sign.invalidBody': 'Este enlace de firma no existe. Pide uno nuevo a la agencia.',
    'sign.expiredTitle': 'Enlace caducado',
    'sign.expiredBody': 'Este enlace ha caducado. Pide uno nuevo a la agencia.',
    'sign.usedTitle': 'Enlace ya usado',
    'sign.usedBody': 'Este enlace ya se usó. Contacta con la agencia si es un error.',
    'sign.lockedTitle': 'Sesión bloqueada',
    'sign.lockedBody': 'Demasiados códigos incorrectos. Pide un enlace nuevo a la agencia.',
    'sign.success': 'Contrato firmado',
    'sign.already': 'Contrato ya firmado',
    'sign.confirmed': 'Tu reserva está confirmada. Lleva tu documento de identidad a la agencia.',
    'sign.download': 'Descargar el PDF',
    'sign.stepContract': 'Contrato',
    'sign.stepVerify': 'Verificación',
    'sign.stepSign': 'Firma',
    'sign.stepDone': 'Listo',
    'sign.client': 'Cliente',
    'sign.license': 'Permiso',
    'sign.vehicle': 'Vehículo',
    'sign.pickup': 'Recogida',
    'sign.dropoff': 'Devolución',
    'sign.duration': 'Duración',
    'sign.days': 'día(s)',
    'sign.subtotal': 'Subtotal',
    'sign.protection': 'Protección',
    'sign.options': 'Opciones',
    'sign.total': 'Total',
    'sign.advance': 'Anticipo',
    'sign.deposit': 'Fianza',
    'sign.terms': 'Condiciones',
    'sign.accept': 'He leído y acepto las condiciones del contrato.',
    'sign.continue': 'Continuar',
    'sign.phoneTitle': 'Verificación del teléfono',
    'sign.phoneSent': 'Se enviará un código al número',
    'sign.phoneLabel': 'Teléfono de la reserva',
    'sign.send': 'Recibir el código',
    'sign.sending': 'Enviando…',
    'sign.demo': 'Modo demostración',
    'sign.codeTitle': 'Código de 6 dígitos',
    'sign.verifying': 'Comprobando…',
    'sign.verify': 'Verificar',
    'sign.backContract': 'Volver al contrato',
    'sign.signTitle': 'Firma',
    'sign.nameLabel': 'Nombre completo, tal como debe figurar en el contrato',
    'sign.namePlaceholder': 'Tu nombre completo',
    'sign.draw': 'Dibuja tu firma debajo',
    'sign.consent': 'Acepto que esta firma junto con el código SMS es mi firma, y que mis datos se usan para gestionar el contrato.',
    'sign.signing': 'Firmando…',
    'sign.sign': 'Firmar el contrato',
    'sign.backVerify': 'Volver a la verificación',
    'sign.needPhone': 'Introduce el teléfono de la reserva.',
    'sign.sendFail': 'No se pudo enviar el código.',
    'sign.sent': 'Código enviado. Caduca en unos minutos.',
    'sign.retry': 'Inténtalo de nuevo.',
    'sign.needCode': 'El código debe tener 6 dígitos.',
    'sign.badCode': 'Código incorrecto.',
    'sign.needName': 'Introduce tu nombre completo.',
    'sign.needDraw': 'Dibuja tu firma en el recuadro.',
    'sign.needConsent': 'Marca el consentimiento para firmar.',
    'sign.badSignature': 'La firma no es válida. Bórrala y vuelve a intentarlo.',
    'sign.signFail': 'No se pudo firmar el contrato.',
    'sign.recorded': 'Firmas registradas',
    'site.catalogError': 'No se pudo cargar la flota. Actualiza la página o contacta con la agencia.',
  },
};

for (const lang of Object.keys(SIGN) as SiteLang[]) {
  add(lang, SIGN[lang]);
}

add('en', { 'sign.pdf': 'Download the contract (PDF)', 'sign.print': 'Open / print the PDF', 'sign.copy': 'A copy was sent to the agency.' });
add('fr', { 'sign.pdf': 'Télécharger le contrat (PDF)', 'sign.print': 'Ouvrir / imprimer le PDF', 'sign.copy': 'Un exemplaire a été transmis à l’agence.' });
add('ar', { 'sign.pdf': 'تنزيل العقد (PDF)', 'sign.print': 'فتح / طباعة العقد (PDF)', 'sign.copy': 'أُرسلت نسخة إلى الوكالة.' });
add('es', { 'sign.pdf': 'Descargar el contrato (PDF)', 'sign.print': 'Abrir / imprimir el PDF', 'sign.copy': 'Se envió una copia a la agencia.' });

// ---------------- landing (SaaS marketing page) ----------------
add('fr', {
  'landing.nav.features': 'Fonctionnalités',
  'landing.nav.how': 'Comment ça marche',
  'landing.nav.pricing': 'Tarifs',
  'landing.nav.faq': 'FAQ',
  'landing.nav.login': 'Se connecter',
  'landing.nav.demo': 'Demander une démo',

  'landing.hero.badge': 'Plateforme SaaS pour agences de location',
  'landing.hero.title': 'Toute votre agence de location, dans un seul espace',
  'landing.hero.subtitle': 'fleetava réunit la flotte, les réservations, les contrats signés, la caisse et le site public. Une console pour tenir l’agence, du devis au retour du véhicule.',
  'landing.hero.ctaPrimary': 'Demander une démo',
  'landing.hero.ctaSecondary': 'Découvrir les fonctionnalités',
  'landing.hero.note': 'Mise en route accompagnée · Sans engagement',

  'landing.stats.modules': 'Modules métier',
  'landing.stats.langs': 'Langues',
  'landing.stats.tenancy': 'Multi-agences',
  'landing.stats.signing': 'Signature électronique',

  'landing.features.title': 'Tout ce qu’il faut pour louer sereinement',
  'landing.features.subtitle': 'De la réservation au contrat signé, fleetava couvre chaque étape de votre activité.',
  'landing.features.fleet.t': 'Flotte & véhicules',
  'landing.features.fleet.d': 'Fiche complète par voiture : tarif, photos, kilométrage, assurance, visite technique et disponibilité.',
  'landing.features.bookings.t': 'Réservations & planning',
  'landing.features.bookings.d': 'Devis, disponibilité en temps réel et planning pour éviter les doubles réservations.',
  'landing.features.clients.t': 'Clients & CRM',
  'landing.features.clients.d': 'Fiches clients, historique de location, permis et documents réunis au même endroit.',
  'landing.features.contracts.t': 'Contrats & signature en ligne',
  'landing.features.contracts.d': 'Générez le contrat, envoyez un lien, le client signe depuis son téléphone. PDF horodaté et archivé.',
  'landing.features.cash.t': 'Caisse, dépenses & amendes',
  'landing.features.cash.d': 'Suivez les encaissements, les dépenses et les contraventions par véhicule et par période.',
  'landing.features.analytics.t': 'Tableau de bord & rapports',
  'landing.features.analytics.d': 'Chiffre d’affaires, taux d’utilisation et performance de la flotte en un coup d’œil.',
  'landing.features.site.t': 'Site public par agence',
  'landing.features.site.d': 'Chaque agence publie son catalogue et reçoit des réservations en ligne.',
  'landing.features.multi.t': 'Multi-agences & rôles',
  'landing.features.multi.d': 'Pilotez plusieurs agences et gérez les accès de votre équipe : propriétaire, réception, etc.',

  'landing.how.title': 'Opérationnel en quelques minutes',
  'landing.how.subtitle': 'Aucune installation. Votre agence est en ligne rapidement.',
  'landing.how.1.t': 'Créez votre agence',
  'landing.how.1.d': 'Renseignez votre profil, votre logo et vos couleurs.',
  'landing.how.2.t': 'Importez votre flotte',
  'landing.how.2.d': 'Ajoutez vos véhicules et vos tarifs, manuellement ou par fichier.',
  'landing.how.3.t': 'Publiez votre site',
  'landing.how.3.d': 'Mettez votre catalogue en ligne et recevez vos premières réservations.',
  'landing.how.4.t': 'Encaissez et suivez',
  'landing.how.4.d': 'Gérez les contrats, la caisse et vos statistiques au quotidien.',

  'landing.preview.title': 'Une console claire pour votre équipe',
  'landing.preview.subtitle': 'Réservations, flotte, contrats, caisse et rapports réunis dans une seule interface.',
  'landing.preview.tagAdmin': 'Console agence',
  'landing.preview.tagPublic': 'Site public',
  'landing.preview.tagPlatform': 'Console plateforme',

  'landing.pricing.title': 'Des tarifs simples',
  'landing.pricing.subtitle': 'Choisissez le plan adapté à la taille de votre agence. Changez à tout moment.',
  'landing.pricing.monthly': '/ mois',
  'landing.pricing.note': 'Prix indicatifs en dirhams marocains (MAD).',
  'landing.pricing.cta': 'Demander une démo',
  'landing.pricing.popular': 'Le plus choisi',
  'landing.plan.starter.d': 'Pour se lancer',
  'landing.plan.starter.f0': '1 agence',
  'landing.plan.starter.f1': 'Site public inclus',
  'landing.plan.starter.f2': 'Contrats et signature',
  'landing.plan.growth.d': 'Pour grandir',
  'landing.plan.growth.f0': 'Plusieurs agences',
  'landing.plan.growth.f1': 'Rôles pour l’équipe',
  'landing.plan.growth.f2': 'Tarifs et caisse',
  'landing.plan.enterprise.d': 'Pour les réseaux',
  'landing.plan.enterprise.f0': 'Nom de domaine personnalisé',
  'landing.plan.enterprise.f1': 'Support prioritaire',
  'landing.plan.enterprise.f2': 'Signature qualifiée',

  'landing.meta.title': 'fleetava — Logiciel de gestion pour agences de location',
  'landing.meta.description': 'fleetava aide les agences de location à gérer la flotte, les réservations, les contrats signés, la caisse et leur site public.',
  'landing.photo.hero': 'SUV blanc garé en extérieur, photographie d’Ambady Kolazhikaran sur Pexels.',
  'landing.photo.fleet': 'Rangée de SUV neufs sur un parking, photographie de Luke Miller sur Pexels.',
  'landing.photo.keys': 'Main tendant des clés de voiture, photographie de Negative Space sur Pexels.',
  'landing.photo.prep': 'Préparation d’une carrosserie blanche au polissoir, photographie de Khunkorn Laowisit sur Pexels.',
  'landing.photo.team': 'Équipe autour d’un ordinateur portable, photographie d’Alena Darmel sur Pexels.',
  'landing.show.fleet.title': 'Chaque voiture a une fiche, pas une ligne perdue',
  'landing.show.fleet.body': 'Tarif, photos, kilométrage, assurance, visite technique et disponibilité restent attachés au véhicule. L’équipe voit ce qui est prêt, ce qui part, et ce qui doit être revu.',
  'landing.show.book.title': 'Une réservation ne peut pas en écraser une autre',
  'landing.show.book.body': 'Le devis, la disponibilité et le planning viennent du même dossier. Le calendrier bloque le créneau déjà pris avant qu’un second contrat parte sur la même voiture.',
  'landing.show.sign.title': 'Le contrat part avec le client, pas dans un tiroir',
  'landing.show.sign.body': 'Vous préparez le contrat dans la console et envoyez un lien. Le client reçoit un code sur le téléphone du dossier, signe à l’écran, et le PDF signé est archivé.',
  'landing.show.cash.title': 'L’acompte, la caution et les dépenses restent liés au dossier',
  'landing.show.cash.body': 'Encaissements, caution, dépenses et contraventions se suivent par véhicule et par période. Le reçu du comptoir reprend les montants du dossier. Ce n’est pas une facture fiscale.',
  'landing.show.prep.title': 'Le départ et le retour laissent une trace',
  'landing.show.prep.body': 'Au départ et au retour, le dossier garde les photos, le kilométrage et le carburant. Un frais de retour modifie le solde dû. La visite technique et la vidange restent visibles dans le suivi d’entretien.',
  'landing.show.team.title': 'Le propriétaire et la réception n’ont pas le même accès',
  'landing.show.team.body': 'Le propriétaire gère l’équipe, les tarifs et les suppressions. La réception traite les réservations, les départs et la caisse. Chaque agence publie aussi son propre site.',
  'landing.preview.caption': 'Aperçu illustratif de la console agence. Les chiffres et les dossiers ne sont pas des données réelles.',
  'landing.preview.fleet': 'Flotte',
  'landing.preview.active': 'En location',
  'landing.preview.ready': 'Au parc',
  'landing.preview.returns': 'Retours',
  'landing.preview.row1t': 'Départ',
  'landing.preview.row1': 'Dacia Duster',
  'landing.preview.row1s': 'Confirmée',
  'landing.preview.row2t': 'Retour',
  'landing.preview.row2': 'Peugeot 208',
  'landing.preview.row2s': 'En cours',
  'landing.preview.row3t': 'Entretien',
  'landing.preview.row3': 'Assurance',
  'landing.preview.row3s': 'À revoir',
  'landing.contact.next': 'Nous revenons vers vous pour organiser la démonstration. Ce formulaire ne déclenche aucun paiement.',
  'landing.pricing.after': 'Demander une démo ne crée pas d’abonnement. Les prix affichés ne sont pas encaissés sur cette page.',
  'landing.footer.photos': 'Photographies',

  'landing.faq.title': 'Questions fréquentes',
  'landing.faq.1.q': 'Qu’est-ce que fleetava ?',
  'landing.faq.1.a': 'fleetava est une plateforme tout-en-un pour les agences de location de voitures : gestion de la flotte, réservations, contrats et signature en ligne, caisse et site public.',
  'landing.faq.2.q': 'Faut-il installer un logiciel ?',
  'landing.faq.2.a': 'Non. fleetava fonctionne dans le navigateur, sur ordinateur comme sur téléphone. Aucune installation n’est nécessaire.',
  'landing.faq.3.q': 'Puis-je garder mon nom de domaine ?',
  'landing.faq.3.a': 'Oui. Votre site public peut utiliser le domaine de votre choix, ou un sous-domaine fourni par fleetava.',
  'landing.faq.4.q': 'Comment fonctionne la signature des contrats ?',
  'landing.faq.4.a': 'Vous générez le contrat depuis la console et envoyez un lien au client. Il vérifie son identité, signe à l’écran, et le PDF signé est archivé.',
  'landing.faq.5.q': 'Puis-je essayer avant de m’engager ?',
  'landing.faq.5.a': 'Oui. Demandez une démo : nous vous montrons la plateforme avec vos cas d’usage, sans engagement.',

  'landing.contact.title': 'Demandez une démo',
  'landing.contact.subtitle': 'Dites-nous en un peu plus sur votre agence. Nous vous recontactons rapidement.',
  'landing.contact.name': 'Nom complet',
  'landing.contact.email': 'Email',
  'landing.contact.phone': 'Téléphone',
  'landing.contact.company': 'Nom de l’agence',
  'landing.contact.city': 'Ville',
  'landing.contact.message': 'Votre besoin (optionnel)',
  'landing.contact.submit': 'Envoyer la demande',
  'landing.contact.sending': 'Envoi…',
  'landing.contact.success': 'Merci ! Votre demande a bien été envoyée. Nous revenons vers vous très vite.',
  'landing.contact.error': 'Envoi impossible pour le moment. Merci de réessayer.',
  'landing.contact.required': 'Champs obligatoires',
  'landing.contact.place.hint': 'Zoomez ou cherchez une ville, puis choisissez une agence de location. Vous pouvez aussi cliquer sur la carte.',
  'landing.contact.place.search': 'Rechercher une ville ou une adresse',
  'landing.contact.place.nearby': 'Agences de location dans cette zone',
  'landing.contact.place.zoom': 'Zoomez pour afficher les agences de location autour de la carte.',
  'landing.contact.place.empty': 'Aucune agence de location connue dans cette zone.',
  'landing.contact.place.looking': 'Lecture du point…',
  'landing.contact.place.failed': 'Ce point n’a pas pu être lu. Choisissez-en un autre.',
  'landing.contact.place.missing': 'Choisissez un point sur la carte.',

  'landing.footer.tagline': 'La plateforme des agences de location de voitures.',
  'landing.footer.product': 'Produit',
  'landing.footer.account': 'Mon espace',
  'landing.footer.contact': 'Contact',
  'landing.footer.rights': 'Tous droits réservés.',

  'login.error.platform': 'Ce compte appartient à la console plateforme.',
  'login.error.agency': 'Ce compte appartient à une agence. Connectez-vous sur le site de cette agence.',
  'login.error.expired': 'Cette session a expiré ou l’accès a été refusé. Reconnectez-vous.',
  'login.error.credentials': 'Email ou mot de passe incorrect.',
  'login.error.codeFormat': 'Saisissez le code à 6 chiffres de votre application d’authentification.',
  'login.error.code': 'Ce code n’est pas valable.',
  'login.error.email': 'L’email n’a pas pu être envoyé.',
  'login.error.mismatch': 'Les nouveaux mots de passe ne correspondent pas.',
  'login.error.resetDone': 'Mot de passe mis à jour. Connectez-vous avec le nouveau. L’authentification à deux facteurs reste active.',
  'login.heading.recover': 'Récupérer l’authentificateur',
  'login.heading.twoFactor': 'Vérification en deux étapes',
  'login.heading.setup': 'Activer la vérification en deux étapes',
  'login.heading.reset': 'Réinitialiser le mot de passe',
  'login.heading.signIn': 'Connexion',
  'login.lead.recover': 'Saisissez le code reçu par email. Un compte plateforme configure ensuite un nouvel authentificateur. Un compte agence se connecte et peut le réactiver depuis le profil.',
  'login.lead.enrolled': 'Saisissez le code de votre authentificateur.',
  'login.lead.setup': 'Scannez le QR code, puis saisissez le premier code.',
  'login.lead.reset': 'Si cet email correspond à un compte, nous envoyons un code. L’adresse ne change pas, et la vérification en deux étapes reste active.',
  'login.field.email': 'Email',
  'login.field.password': 'Mot de passe',
  'login.field.newPassword': 'Nouveau mot de passe',
  'login.field.confirmPassword': 'Confirmer le mot de passe',
  'login.field.emailCode': 'Code email',
  'login.field.authenticator': 'Code d’authentification',
  'login.hint.password': '12 caractères au minimum.',
  'login.remember': 'Se souvenir de moi sur ce navigateur',
  'login.forgot': 'Mot de passe oublié ?',
  'login.submit': 'Se connecter',
  'login.submitting': 'Connexion…',
  'login.sendCode': 'Envoyer le code',
  'login.sending': 'Envoi…',
  'login.save': 'Enregistrer',
  'login.saving': 'Enregistrement…',
  'login.verify': 'Vérifier le code',
  'login.verifying': 'Vérification…',
  'login.confirmCode': 'Confirmer le code email',
  'login.backAuth': 'Retour à l’authentificateur',
  'login.lostAuth': 'J’ai perdu mon authentificateur',
  'login.otherAccount': 'Utiliser un autre compte',
  'login.backSignIn': 'Retour à la connexion',
  'login.showPassword': 'Afficher le mot de passe',
  'login.hidePassword': 'Masquer le mot de passe',
  'login.demoCode': 'Code de démonstration',
  'login.platform.kicker': 'Console plateforme',
  'login.platform.title': 'Toutes les agences, une seule console.',
  'login.platform.body': 'Cette connexion est réservée au propriétaire de la plateforme. Elle n’ouvre pas le bureau d’une agence tant que vous n’empruntez pas son accès.',
  'login.platform.registry': 'Registre',
  'login.platform.impersonation': 'Accès agence · 15 min',
  'login.platform.audited': 'Audité',
  'login.platform.back': 'Retour au site',
  'login.agency.kicker': 'Bureau agence',
  'login.agency.title': 'Connexion du bureau',
  'login.agency.body': 'Une seule porte pour les deux rôles.',
  'login.agency.owner': 'Propriétaire',
  'login.agency.ownerDetail': 'Équipe, tarifs, suppressions, marge',
  'login.agency.desk': 'Réception',
  'login.agency.deskDetail': 'Réservations, départs, caisse',
  'login.agency.ready': 'Prêt',
  'login.agency.roles': 'Propriétaire et réception',
  'login.agency.permissions': 'Les droits s’appliquent après la connexion',
  'login.agency.renting': 'Vous louez une voiture ?',
  'login.agency.account': 'Ouvrir votre compte',
  'login.agency.site': 'retour au site',
});

add('en', {
  'landing.nav.features': 'Features',
  'landing.nav.how': 'How it works',
  'landing.nav.pricing': 'Pricing',
  'landing.nav.faq': 'FAQ',
  'landing.nav.login': 'Sign in',
  'landing.nav.demo': 'Request a demo',

  'landing.hero.badge': 'SaaS platform for car rental agencies',
  'landing.hero.title': 'Your entire rental agency, in one place',
  'landing.hero.subtitle': 'fleetava brings the fleet, bookings, signed contracts, cash desk and public website into one console. Run the agency from the quote through to the vehicle return.',
  'landing.hero.ctaPrimary': 'Request a demo',
  'landing.hero.ctaSecondary': 'Explore the features',
  'landing.hero.note': 'Guided onboarding · No commitment',

  'landing.stats.modules': 'Business modules',
  'landing.stats.langs': 'Languages',
  'landing.stats.tenancy': 'Multi-agency',
  'landing.stats.signing': 'E-signing',

  'landing.features.title': 'Everything you need to rent with confidence',
  'landing.features.subtitle': 'From booking to signed contract, fleetava covers every step of your business.',
  'landing.features.fleet.t': 'Fleet & vehicles',
  'landing.features.fleet.d': 'A full record per car: rate, photos, mileage, insurance, roadworthiness and availability.',
  'landing.features.bookings.t': 'Bookings & calendar',
  'landing.features.bookings.d': 'Quotes, real-time availability and a calendar that prevents double bookings.',
  'landing.features.clients.t': 'Clients & CRM',
  'landing.features.clients.d': 'Customer records, rental history, licence and documents all in one place.',
  'landing.features.contracts.t': 'Contracts & e-signing',
  'landing.features.contracts.d': 'Generate the contract, send a link, the customer signs on their phone. Time-stamped, archived PDF.',
  'landing.features.cash.t': 'Cash, expenses & fines',
  'landing.features.cash.d': 'Track payments, expenses and traffic fines by vehicle and by period.',
  'landing.features.analytics.t': 'Dashboard & reports',
  'landing.features.analytics.d': 'Revenue, utilisation and fleet performance at a glance.',
  'landing.features.site.t': 'Per-agency public website',
  'landing.features.site.d': 'Each agency publishes its own catalogue and takes bookings online.',
  'landing.features.multi.t': 'Multi-agency & roles',
  'landing.features.multi.d': 'Run several agencies and manage team access: owner, front desk, and more.',

  'landing.how.title': 'Up and running in minutes',
  'landing.how.subtitle': 'Nothing to install. Your agency goes live quickly.',
  'landing.how.1.t': 'Create your agency',
  'landing.how.1.d': 'Add your profile, logo and brand colours.',
  'landing.how.2.t': 'Import your fleet',
  'landing.how.2.d': 'Add your vehicles and rates, manually or from a file.',
  'landing.how.3.t': 'Publish your site',
  'landing.how.3.d': 'Put your catalogue online and take your first bookings.',
  'landing.how.4.t': 'Get paid and track',
  'landing.how.4.d': 'Manage contracts, cash and your reports day to day.',

  'landing.preview.title': 'A clean console for your team',
  'landing.preview.subtitle': 'Bookings, fleet, contracts, cash and reports in a single interface.',
  'landing.preview.tagAdmin': 'Agency console',
  'landing.preview.tagPublic': 'Public website',
  'landing.preview.tagPlatform': 'Platform console',

  'landing.pricing.title': 'Simple pricing',
  'landing.pricing.subtitle': 'Pick the plan that fits your agency. Change any time.',
  'landing.pricing.monthly': '/ month',
  'landing.pricing.note': 'Indicative prices in Moroccan dirham (MAD).',
  'landing.pricing.cta': 'Request a demo',
  'landing.pricing.popular': 'Most popular',
  'landing.plan.starter.d': 'To get started',
  'landing.plan.starter.f0': '1 agency',
  'landing.plan.starter.f1': 'Public website included',
  'landing.plan.starter.f2': 'Contracts and signing',
  'landing.plan.growth.d': 'To grow',
  'landing.plan.growth.f0': 'Multiple agencies',
  'landing.plan.growth.f1': 'Team roles',
  'landing.plan.growth.f2': 'Rates and cash desk',
  'landing.plan.enterprise.d': 'For networks',
  'landing.plan.enterprise.f0': 'Custom domain',
  'landing.plan.enterprise.f1': 'Priority support',
  'landing.plan.enterprise.f2': 'Qualified signature',

  'landing.meta.title': 'fleetava — Rental agency management software',
  'landing.meta.description': 'fleetava helps car rental agencies run their fleet, bookings, signed contracts, cash desk and public website.',
  'landing.photo.hero': 'White SUV parked outdoors. Photograph by Ambady Kolazhikaran on Pexels.',
  'landing.photo.fleet': 'A row of new SUVs in a parking lot. Photograph by Luke Miller on Pexels.',
  'landing.photo.keys': 'A hand holding out car keys. Photograph by Negative Space on Pexels.',
  'landing.photo.prep': 'Preparing a white car with a polishing tool. Photograph by Khunkorn Laowisit on Pexels.',
  'landing.photo.team': 'A team working around a laptop. Photograph by Alena Darmel on Pexels.',
  'landing.show.fleet.title': 'Every car has a record, not a lost spreadsheet row',
  'landing.show.fleet.body': 'Rate, photos, mileage, insurance, roadworthiness and availability stay on the vehicle. The team can see what is ready, what is out, and what needs attention.',
  'landing.show.book.title': 'One booking cannot overwrite another',
  'landing.show.book.body': 'The quote, availability and calendar come from the same file. A slot that is already taken is blocked before a second contract goes out on the same car.',
  'landing.show.sign.title': 'The contract leaves with the customer',
  'landing.show.sign.body': 'You prepare the contract in the console and send a link. The customer receives a code on the booking phone, signs on screen, and the signed PDF is archived.',
  'landing.show.cash.title': 'The advance, the deposit and the costs stay on the file',
  'landing.show.cash.body': 'Payments, the security deposit, expenses and fines are tracked by vehicle and by period. The desk receipt uses the amounts on the booking. It is not a tax invoice.',
  'landing.show.prep.title': 'Pickup and return leave a record',
  'landing.show.prep.body': 'At handover and return, the file keeps the photos, mileage and fuel. A return charge changes the balance due. Roadworthiness and oil service stay visible in maintenance.',
  'landing.show.team.title': 'The owner and the front desk do not share the same access',
  'landing.show.team.body': 'The owner manages the team, rates and deletions. The front desk handles bookings, handovers and the cash desk. Each agency also publishes its own website.',
  'landing.preview.caption': 'Illustrative agency desk. The figures and files are not live agency data.',
  'landing.preview.fleet': 'Fleet',
  'landing.preview.active': 'On rent',
  'landing.preview.ready': 'In the yard',
  'landing.preview.returns': 'Returns',
  'landing.preview.row1t': 'Pickup',
  'landing.preview.row1': 'Dacia Duster',
  'landing.preview.row1s': 'Confirmed',
  'landing.preview.row2t': 'Return',
  'landing.preview.row2': 'Peugeot 208',
  'landing.preview.row2s': 'On rent',
  'landing.preview.row3t': 'Maintenance',
  'landing.preview.row3': 'Insurance',
  'landing.preview.row3s': 'Due',
  'landing.contact.next': 'We contact you to arrange the demonstration. This form does not take a payment.',
  'landing.pricing.after': 'Requesting a demo does not start a subscription. The prices on this page are not collected here.',
  'landing.footer.photos': 'Photographs',

  'landing.faq.title': 'Frequently asked questions',
  'landing.faq.1.q': 'What is fleetava?',
  'landing.faq.1.a': 'fleetava is an all-in-one platform for car rental agencies: fleet management, bookings, contracts and e-signing, cash desk and a public website.',
  'landing.faq.2.q': 'Do I need to install software?',
  'landing.faq.2.a': 'No. fleetava runs in the browser, on desktop and mobile. There is nothing to install.',
  'landing.faq.3.q': 'Can I keep my own domain?',
  'landing.faq.3.a': 'Yes. Your public site can use the domain of your choice, or a subdomain provided by fleetava.',
  'landing.faq.4.q': 'How does contract signing work?',
  'landing.faq.4.a': 'You generate the contract in the console and send a link to the customer. They verify their identity, sign on screen, and the signed PDF is archived.',
  'landing.faq.5.q': 'Can I try it before committing?',
  'landing.faq.5.a': 'Yes. Request a demo: we walk you through the platform with your own use cases, with no commitment.',

  'landing.contact.title': 'Request a demo',
  'landing.contact.subtitle': 'Tell us a little about your agency. We will get back to you shortly.',
  'landing.contact.name': 'Full name',
  'landing.contact.email': 'Email',
  'landing.contact.phone': 'Phone',
  'landing.contact.company': 'Agency name',
  'landing.contact.city': 'City',
  'landing.contact.message': 'What you need (optional)',
  'landing.contact.submit': 'Send request',
  'landing.contact.sending': 'Sending…',
  'landing.contact.success': 'Thank you! Your request was sent. We will get back to you very soon.',
  'landing.contact.error': 'Could not send right now. Please try again.',
  'landing.contact.required': 'Required fields',
  'landing.contact.place.hint': 'Zoom or search for a city, then choose a rental agency. You can also click the map.',
  'landing.contact.place.search': 'Search for a city or an address',
  'landing.contact.place.nearby': 'Rental agencies in this view',
  'landing.contact.place.zoom': 'Zoom in to list rental agencies around the map.',
  'landing.contact.place.empty': 'No known rental agency in this view.',
  'landing.contact.place.looking': 'Reading this point…',
  'landing.contact.place.failed': 'This point could not be read. Try another.',
  'landing.contact.place.missing': 'Choose a point on the map.',

  'landing.footer.tagline': 'The platform for car rental agencies.',
  'landing.footer.product': 'Product',
  'landing.footer.account': 'My space',
  'landing.footer.contact': 'Contact',
  'landing.footer.rights': 'All rights reserved.',

  'login.error.platform': 'This account belongs to the platform console.',
  'login.error.agency': 'This account belongs to an agency. Sign in on that agency site.',
  'login.error.expired': 'This session expired or access was refused. Sign in again.',
  'login.error.credentials': 'Email or password is incorrect.',
  'login.error.codeFormat': 'Enter the 6-digit code from your authenticator app.',
  'login.error.code': 'That code is not valid.',
  'login.error.email': 'The email could not be sent.',
  'login.error.mismatch': 'The new passwords do not match.',
  'login.error.resetDone': 'Password updated. Sign in with the new password. Two-factor authentication is unchanged.',
  'login.heading.recover': 'Recover authenticator',
  'login.heading.twoFactor': 'Two-factor verification',
  'login.heading.setup': 'Set up two-factor',
  'login.heading.reset': 'Reset password',
  'login.heading.signIn': 'Sign in',
  'login.lead.recover': 'Enter the email code. A platform account sets up a new authenticator next. An agency account signs in and can turn it on again from the profile.',
  'login.lead.enrolled': 'Enter the code from your authenticator.',
  'login.lead.setup': 'Scan the QR code, then enter the first code.',
  'login.lead.reset': 'If this email belongs to an account, we send a code. The address does not change, and two-factor stays on.',
  'login.field.email': 'Email',
  'login.field.password': 'Password',
  'login.field.newPassword': 'New password',
  'login.field.confirmPassword': 'Confirm new password',
  'login.field.emailCode': 'Email code',
  'login.field.authenticator': 'Authenticator code',
  'login.hint.password': 'At least 12 characters.',
  'login.remember': 'Remember me on this browser',
  'login.forgot': 'Forgot password?',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.sendCode': 'Send code',
  'login.sending': 'Sending…',
  'login.save': 'Reset password',
  'login.saving': 'Saving…',
  'login.verify': 'Verify code',
  'login.verifying': 'Verifying…',
  'login.confirmCode': 'Confirm email code',
  'login.backAuth': 'Back to the authenticator',
  'login.lostAuth': 'I lost my authenticator',
  'login.otherAccount': 'Use another account',
  'login.backSignIn': 'Back to sign-in',
  'login.showPassword': 'Show password',
  'login.hidePassword': 'Hide password',
  'login.demoCode': 'Demo code',
  'login.platform.kicker': 'Platform console',
  'login.platform.title': 'Every agency, one console.',
  'login.platform.body': 'This sign-in is for the platform owner. It does not open an agency desk until you impersonate that agency.',
  'login.platform.registry': 'Registry',
  'login.platform.impersonation': 'Impersonation · 15 min',
  'login.platform.audited': 'Audited',
  'login.platform.back': 'Back to the marketing site',
  'login.agency.kicker': 'Agency desk',
  'login.agency.title': 'Desk sign-in',
  'login.agency.body': 'One door for both roles.',
  'login.agency.owner': 'Owner',
  'login.agency.ownerDetail': 'Staff, tariffs, deletes, profit',
  'login.agency.desk': 'Front desk',
  'login.agency.deskDetail': 'Bookings, handovers, cash',
  'login.agency.ready': 'Ready',
  'login.agency.roles': 'Owner and front desk',
  'login.agency.permissions': 'Permissions apply after sign-in',
  'login.agency.renting': 'Renting a car?',
  'login.agency.account': 'Open your account',
  'login.agency.site': 'back to the site',
});

add('ar', {
  'landing.nav.features': 'المزايا',
  'landing.nav.how': 'كيف يعمل',
  'landing.nav.pricing': 'الأسعار',
  'landing.nav.faq': 'الأسئلة',
  'landing.nav.login': 'تسجيل الدخول',
  'landing.nav.demo': 'اطلب عرضًا',

  'landing.hero.badge': 'منصة للوكالات التي تؤجّر السيارات',
  'landing.hero.title': 'وكالتك كلها في مكان واحد',
  'landing.hero.subtitle': 'تجمع fleetava الأسطول والحجوزات والعقود الموقَّعة والصندوق والموقع العام في لوحة واحدة. من عرض السعر حتى إعادة السيارة.',
  'landing.hero.ctaPrimary': 'اطلب عرضًا',
  'landing.hero.ctaSecondary': 'تعرّف على المزايا',
  'landing.hero.note': 'انطلاق بمرافقة · دون التزام',

  'landing.stats.modules': 'وحدات العمل',
  'landing.stats.langs': 'اللغات',
  'landing.stats.tenancy': 'عدة وكالات',
  'landing.stats.signing': 'توقيع إلكتروني',

  'landing.features.title': 'ما تحتاجه الوكالة لتؤجّر بهدوء',
  'landing.features.subtitle': 'من الحجز إلى العقد الموقَّع، تغطي fleetava خطوات العمل.',
  'landing.features.fleet.t': 'الأسطول والسيارات',
  'landing.features.fleet.d': 'بطاقة لكل سيارة: السعر والصور والعداد والتأمين والفحص التقني والتوفر.',
  'landing.features.bookings.t': 'الحجوزات والجدول',
  'landing.features.bookings.d': 'عروض أسعار وتوفر مباشر وجدول يمنع الحجز المزدوج.',
  'landing.features.clients.t': 'الزبائن',
  'landing.features.clients.d': 'ملفات الزبائن وسجل الإيجار ورخصة السياقة والوثائق في مكان واحد.',
  'landing.features.contracts.t': 'العقود والتوقيع',
  'landing.features.contracts.d': 'تُنشئ العقد وترسل رابطًا، ويوقّع الزبون من هاتفه. يُحفظ ملف PDF.',
  'landing.features.cash.t': 'الصندوق والمصاريف والمخالفات',
  'landing.features.cash.d': 'تابع المقبوضات والمصاريف والمخالفات حسب السيارة والفترة.',
  'landing.features.analytics.t': 'التقارير',
  'landing.features.analytics.d': 'المداخيل ونسبة الاستعمال وأداء الأسطول في نظرة واحدة.',
  'landing.features.site.t': 'موقع لكل وكالة',
  'landing.features.site.d': 'تنشر كل وكالة كتالوجها وتستقبل الحجوزات عبر الإنترنت.',
  'landing.features.multi.t': 'عدة وكالات وصلاحيات',
  'landing.features.multi.d': 'أدر أكثر من وكالة وصلاحيات الفريق: المالك والاستقبال.',

  'landing.how.title': 'جاهز للعمل خلال دقائق',
  'landing.how.subtitle': 'لا تثبيت. تُنشر الوكالة بسرعة.',
  'landing.how.1.t': 'أنشئ الوكالة',
  'landing.how.1.d': 'أضف الملف والشعار والألوان.',
  'landing.how.2.t': 'أدخل الأسطول',
  'landing.how.2.d': 'أضف السيارات والأسعار يدويًا أو من ملف.',
  'landing.how.3.t': 'انشر الموقع',
  'landing.how.3.d': 'اعرض الكتالوج واستقبل الحجوزات.',
  'landing.how.4.t': 'تابع الصندوق',
  'landing.how.4.d': 'أدر العقود والصندوق والتقارير يوميًا.',

  'landing.preview.title': 'لوحة واضحة للفريق',
  'landing.preview.subtitle': 'الحجوزات والأسطول والعقود والصندوق والتقارير في واجهة واحدة.',
  'landing.preview.tagAdmin': 'لوحة الوكالة',
  'landing.preview.tagPublic': 'الموقع العام',
  'landing.preview.tagPlatform': 'لوحة المنصة',

  'landing.pricing.title': 'أسعار واضحة',
  'landing.pricing.subtitle': 'اختر الخطة المناسبة لحجم الوكالة. يمكن التغيير لاحقًا.',
  'landing.pricing.monthly': '/ شهر',
  'landing.pricing.note': 'أسعار إرشادية بالدرهم المغربي (MAD).',
  'landing.pricing.cta': 'اطلب عرضًا',
  'landing.pricing.popular': 'الأكثر اختيارًا',
  'landing.plan.starter.d': 'للبداية',
  'landing.plan.starter.f0': 'وكالة واحدة',
  'landing.plan.starter.f1': 'موقع عام',
  'landing.plan.starter.f2': 'عقود وتوقيع',
  'landing.plan.growth.d': 'للنمو',
  'landing.plan.growth.f0': 'عدة وكالات',
  'landing.plan.growth.f1': 'صلاحيات الفريق',
  'landing.plan.growth.f2': 'التعريفة والصندوق',
  'landing.plan.enterprise.d': 'للشبكات',
  'landing.plan.enterprise.f0': 'نطاق خاص',
  'landing.plan.enterprise.f1': 'دعم أولوي',
  'landing.plan.enterprise.f2': 'توقيع مؤهَّل',

  'landing.meta.title': 'fleetava — برنامج إدارة وكالات كراء السيارات',
  'landing.meta.description': 'تساعد fleetava وكالات الكراء على إدارة الأسطول والحجوزات والعقود الموقَّعة والصندوق والموقع العام.',
  'landing.photo.hero': 'سيارة دفع رباعي بيضاء. صورة Ambady Kolazhikaran على Pexels.',
  'landing.photo.fleet': 'صف من سيارات الدفع الرباعي في موقف. صورة Luke Miller على Pexels.',
  'landing.photo.keys': 'يد تقدّم مفاتيح سيارة. صورة Negative Space على Pexels.',
  'landing.photo.prep': 'تجهيز هيكل أبيض بأداة تلميع. صورة Khunkorn Laowisit على Pexels.',
  'landing.photo.team': 'فريق حول حاسوب محمول. صورة Alena Darmel على Pexels.',
  'landing.show.fleet.title': 'لكل سيارة بطاقة، لا سطر ضائع',
  'landing.show.fleet.body': 'يبقى السعر والصور والعداد والتأمين والفحص التقني والتوفر مربوطين بالسيارة. يرى الفريق ما هو جاهز وما خرج وما يحتاج مراجعة.',
  'landing.show.book.title': 'لا يحجز عقد مكان عقد آخر',
  'landing.show.book.body': 'يأتي عرض السعر والتوفر والجدول من الملف نفسه. الفترة المأخوذة تُمنع قبل أن يخرج عقد ثانٍ على السيارة نفسها.',
  'landing.show.sign.title': 'العقد يغادر مع الزبون',
  'landing.show.sign.body': 'تُجهّز العقد من اللوحة وترسل رابطًا. يصل رمز إلى هاتف الحجز، يوقّع الزبون على الشاشة، ويُحفظ ملف PDF.',
  'landing.show.cash.title': 'التسبيق والضمان والمصاريف تبقى في الملف',
  'landing.show.cash.body': 'تُتابع المقبوضات والضمان والمصاريف والمخالفات حسب السيارة والفترة. وصل الصندوق يستعمل مبالغ الحجز. ليس فاتورة ضريبية.',
  'landing.show.prep.title': 'التسليم والإرجاع يتركان أثرًا',
  'landing.show.prep.body': 'عند الخروج والعودة يحتفظ الملف بالصور والعداد والوقود. رسم الإرجاع يغيّر المبلغ المتبقي. الفحص التقني وتغيير الزيت يظهران في الصيانة.',
  'landing.show.team.title': 'المالك والاستقبال لا يريان الشيء نفسه',
  'landing.show.team.body': 'يدير المالك الفريق والتعريفة والحذف. يتولى الاستقبال الحجوزات والتسليم والصندوق. وتنشر كل وكالة موقعها.',
  'landing.preview.caption': 'مثال للوحة الوكالة. الأرقام والملفات ليست بيانات حقيقية.',
  'landing.preview.fleet': 'الأسطول',
  'landing.preview.active': 'في الكراء',
  'landing.preview.ready': 'في الموقف',
  'landing.preview.returns': 'العودة',
  'landing.preview.row1t': 'خروج',
  'landing.preview.row1': 'Dacia Duster',
  'landing.preview.row1s': 'مؤكدة',
  'landing.preview.row2t': 'عودة',
  'landing.preview.row2': 'Peugeot 208',
  'landing.preview.row2s': 'جارية',
  'landing.preview.row3t': 'صيانة',
  'landing.preview.row3': 'التأمين',
  'landing.preview.row3s': 'للمراجعة',
  'landing.contact.next': 'نتصل بك لترتيب العرض. هذا النموذج لا يطلب أي دفع.',
  'landing.pricing.after': 'طلب العرض لا يفتح اشتراكًا. الأسعار في هذه الصفحة لا تُحصَّل هنا.',
  'landing.footer.photos': 'الصور',

  'landing.faq.title': 'أسئلة متكررة',
  'landing.faq.1.q': 'ما هي fleetava؟',
  'landing.faq.1.a': 'fleetava منصة لوكالات كراء السيارات: الأسطول والحجوزات والعقود والتوقيع والصندوق وموقع عام.',
  'landing.faq.2.q': 'هل يلزم تثبيت برنامج؟',
  'landing.faq.2.a': 'لا. تعمل fleetava في المتصفح على الحاسوب والهاتف. لا شيء للتثبيت.',
  'landing.faq.3.q': 'هل أحتفظ بنطاقي؟',
  'landing.faq.3.a': 'نعم. يمكن للموقع العام أن يستعمل النطاق الذي تختاره، أو نطاقًا فرعيًا من fleetava.',
  'landing.faq.4.q': 'كيف يتم توقيع العقد؟',
  'landing.faq.4.a': 'تُنشئ العقد من اللوحة وترسل رابطًا. يتحقق الزبون برمز على هاتف الحجز، يوقّع على الشاشة، ويُحفظ ملف PDF.',
  'landing.faq.5.q': 'هل يمكن التجربة قبل الالتزام؟',
  'landing.faq.5.a': 'نعم. اطلب عرضًا ونعرض المنصة على حالات وكالتك، دون التزام.',

  'landing.contact.title': 'اطلب عرضًا',
  'landing.contact.subtitle': 'أخبرنا قليلًا عن الوكالة. نعود إليك قريبًا.',
  'landing.contact.name': 'الاسم الكامل',
  'landing.contact.email': 'البريد',
  'landing.contact.phone': 'الهاتف',
  'landing.contact.company': 'اسم الوكالة',
  'landing.contact.city': 'المدينة',
  'landing.contact.message': 'ما تحتاجه (اختياري)',
  'landing.contact.submit': 'إرسال الطلب',
  'landing.contact.sending': 'جارٍ الإرسال…',
  'landing.contact.success': 'شكرًا. وصل الطلب وسنعود إليك قريبًا.',
  'landing.contact.error': 'تعذر الإرسال الآن. أعد المحاولة.',
  'landing.contact.required': 'حقول إلزامية',
  'landing.contact.place.hint': 'قرّب الخريطة أو ابحث عن مدينة، ثم اختر وكالة كراء. يمكنك أيضًا النقر على الخريطة.',
  'landing.contact.place.search': 'ابحث عن مدينة أو عنوان',
  'landing.contact.place.nearby': 'وكالات الكراء في هذه المنطقة',
  'landing.contact.place.zoom': 'قرّب الخريطة لعرض وكالات الكراء حولها.',
  'landing.contact.place.empty': 'لا توجد وكالة كراء معروفة في هذه المنطقة.',
  'landing.contact.place.looking': 'جارٍ قراءة النقطة…',
  'landing.contact.place.failed': 'تعذرت قراءة هذه النقطة. اختر نقطة أخرى.',
  'landing.contact.place.missing': 'اختر نقطة على الخريطة.',

  'landing.footer.tagline': 'منصة وكالات كراء السيارات.',
  'landing.footer.product': 'المنتج',
  'landing.footer.account': 'حسابي',
  'landing.footer.contact': 'اتصال',
  'landing.footer.rights': 'كل الحقوق محفوظة.',

  'login.error.platform': 'هذا الحساب يخص لوحة المنصة.',
  'login.error.agency': 'هذا الحساب يخص وكالة. سجّل الدخول من موقع تلك الوكالة.',
  'login.error.expired': 'انتهت هذه الجلسة أو رُفض الوصول. سجّل الدخول من جديد.',
  'login.error.credentials': 'البريد أو كلمة المرور غير صحيحة.',
  'login.error.codeFormat': 'أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.',
  'login.error.code': 'هذا الرمز غير صالح.',
  'login.error.email': 'تعذر إرسال البريد.',
  'login.error.mismatch': 'كلمتا المرور الجديدتان غير متطابقتين.',
  'login.error.resetDone': 'تم تحديث كلمة المرور. سجّل الدخول بالجديدة. التحقق بخطوتين يبقى كما هو.',
  'login.heading.recover': 'استعادة المصادقة',
  'login.heading.twoFactor': 'التحقق بخطوتين',
  'login.heading.setup': 'إعداد التحقق بخطوتين',
  'login.heading.reset': 'إعادة تعيين كلمة المرور',
  'login.heading.signIn': 'تسجيل الدخول',
  'login.lead.recover': 'أدخل رمز البريد. حساب المنصة يجهّز بعدها مصادقًا جديدًا. حساب الوكالة يدخل ويمكنه إعادة التفعيل من الملف.',
  'login.lead.enrolled': 'أدخل الرمز من تطبيق المصادقة.',
  'login.lead.setup': 'امسح رمز QR ثم أدخل أول رمز.',
  'login.lead.reset': 'إذا كان هذا البريد لحساب، نرسل رمزًا. العنوان لا يتغير، والتحقق بخطوتين يبقى مفعلًا.',
  'login.field.email': 'البريد',
  'login.field.password': 'كلمة المرور',
  'login.field.newPassword': 'كلمة المرور الجديدة',
  'login.field.confirmPassword': 'تأكيد كلمة المرور',
  'login.field.emailCode': 'رمز البريد',
  'login.field.authenticator': 'رمز المصادقة',
  'login.hint.password': '12 حرفًا على الأقل.',
  'login.remember': 'تذكّرني في هذا المتصفح',
  'login.forgot': 'نسيت كلمة المرور؟',
  'login.submit': 'دخول',
  'login.submitting': 'جارٍ الدخول…',
  'login.sendCode': 'إرسال الرمز',
  'login.sending': 'جارٍ الإرسال…',
  'login.save': 'حفظ كلمة المرور',
  'login.saving': 'جارٍ الحفظ…',
  'login.verify': 'تأكيد الرمز',
  'login.verifying': 'جارٍ التحقق…',
  'login.confirmCode': 'تأكيد رمز البريد',
  'login.backAuth': 'العودة إلى المصادقة',
  'login.lostAuth': 'فقدت تطبيق المصادقة',
  'login.otherAccount': 'استخدام حساب آخر',
  'login.backSignIn': 'العودة إلى الدخول',
  'login.showPassword': 'إظهار كلمة المرور',
  'login.hidePassword': 'إخفاء كلمة المرور',
  'login.demoCode': 'رمز التجربة',
  'login.platform.kicker': 'لوحة المنصة',
  'login.platform.title': 'كل الوكالات في لوحة واحدة.',
  'login.platform.body': 'هذا الدخول لصاحب المنصة. لا يفتح مكتب وكالة إلا بعد الدخول باسمها.',
  'login.platform.registry': 'السجل',
  'login.platform.impersonation': 'دخول الوكالة · 15 د',
  'login.platform.audited': 'مُراجع',
  'login.platform.back': 'العودة إلى الموقع',
  'login.agency.kicker': 'مكتب الوكالة',
  'login.agency.title': 'دخول المكتب',
  'login.agency.body': 'باب واحد للدورين.',
  'login.agency.owner': 'المالك',
  'login.agency.ownerDetail': 'الفريق والتعريفة والحذف والهامش',
  'login.agency.desk': 'الاستقبال',
  'login.agency.deskDetail': 'الحجوزات والتسليم والصندوق',
  'login.agency.ready': 'جاهز',
  'login.agency.roles': 'المالك والاستقبال',
  'login.agency.permissions': 'الصلاحيات تُطبَّق بعد الدخول',
  'login.agency.renting': 'تؤجّر سيارة؟',
  'login.agency.account': 'افتح حسابك',
  'login.agency.site': 'العودة إلى الموقع',
});

export function translationGaps(lang: SiteLang, prefix: string): string[] {
  return Object.keys(DICTS.en).filter((key) => key.startsWith(prefix) && !DICTS[lang][key]);
}

export function useLocale(): LocaleCtx {
  return useContext(LocaleContext);
}

/** Append-only helper for conversion scripts (not used at runtime). */
export function __dictSize(): number {
  return Object.keys(DICTS.en).length;
}
